/**
 * EBC Fahrzeug-Finder — Prototyp (Dawn).
 *
 * Datenquellen (alle statisch vom Shopify-CDN, siehe Pipeline):
 *   - #finder-config (JSON-Script-Tag aus Shop-Metafeld finder.config):
 *     { v, release, manifestUrl, makes: [[slug, label, typCount], …],
 *       moto?: { manifestUrl, makes: [[slug, label, katalogBitmaske], …] } }
 *   - #finder-stock (JSON-Script-Tag aus Shop-Metafeld finder.stock):
 *     { url, stand, anzahl } — die Datei dahinter listet NUR abweichende
 *     Artikel ({ c: {nr:code}, s: {sku:nr}, t: {sku:text} }); alles, was dort
 *     fehlt, hat die Standard-Lieferzeit. Wird täglich vom Lagerabgleich neu
 *     geschrieben (server/pipeline/StockSync.php).
 *   - #finder-prices (JSON-Script-Tag aus Shop-Metafeld finder.prices):
 *     { url, stand, anzahl } — die Datei dahinter ({ p: {sku:cent}, u? })
 *     enthält die Shop-Preise, die über JTL kommen. Täglich vom
 *     Preisabgleich geschrieben (server/pipeline/PreisSync.php); sie
 *     überdeckt die Listenpreise des Releases.
 *   - PKW-Manifest: lines[], vehicles{slug:[urls]}, fit{buckets, files[]},
 *     hsn{files{prefix:url}}, prices(url) — Listenpreise (VK-Brutto = UVP)
 *   - Moto-Manifest: gruppen[[key,label]], vehicles{slug:[urls]},
 *     fit{buckets, files[]}, prices(url mit { linien:[[label,serie]], skus })
 *
 * Fahrzeug-Zustand in localStorage:
 *   PKW  "ebc:finder:v1":      { release, vehicle:{tecId, make, label, hsnTsn?}, skus:[…], savedAt }
 *   Moto "ebc:finder:moto:v1": { release, vehicle:{vehId, make, label, katalog}, skus:[…], savedAt }
 */
(() => {
  'use strict';

  const configEl = document.getElementById('finder-config');
  const CONFIG = configEl ? JSON.parse(configEl.textContent) : null;
  const HAS_MOTO = !!(CONFIG && CONFIG.moto && CONFIG.moto.manifestUrl);
  const stockEl = document.getElementById('finder-stock');
  const STOCK_CFG = stockEl ? JSON.parse(stockEl.textContent) : null;
  const pricesEl = document.getElementById('finder-prices');
  const PRICES_CFG = pricesEl ? JSON.parse(pricesEl.textContent) : null;
  const LS_KEY = 'ebc:finder:v1';
  const LS_KEY_MOTO = 'ebc:finder:moto:v1';
  const LS_KEY_AREA = 'ebc:finder:area';
  const FINDER_PAGE = '/pages/fahrzeug-finder';

  // --- slugify: MUSS identisch zur Pipeline sein (pipeline/src/lib/text.ts) ---
  const DEBURR = {
    ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss',
    é: 'e', è: 'e', ê: 'e', ë: 'e', á: 'a', à: 'a', â: 'a', ç: 'c',
    í: 'i', ì: 'i', î: 'i', ó: 'o', ò: 'o', ô: 'o', ú: 'u', ù: 'u', û: 'u',
    ñ: 'n', å: 'a', ø: 'o', æ: 'ae', œ: 'oe', š: 's', ž: 'z', č: 'c', ć: 'c', ý: 'y',
  };
  function slugify(s) {
    let out = '';
    for (const ch of s.toLowerCase()) {
      if (DEBURR[ch] !== undefined) out += DEBURR[ch];
      else if (/[a-z0-9]/.test(ch)) out += ch;
      else out += '-';
    }
    return out.replace(/-+/g, '-').replace(/^-|-$/g, '') || 'x';
  }
  const productUrl = (sku) => `/products/ebc-${slugify(sku)}`;

  // Artikelnummer im Shop = "EBC-" + Herstellernummer. Finder-Daten, Handles
  // und Lagerdatei führen die Nummer OHNE Präfix, die Liquid-Templates
  // (data-sku) und /products/*.js die Shopify-SKU MIT Präfix.
  const SKU_PREFIX = 'EBC-';
  const shopSku = (sku) => SKU_PREFIX + sku;
  /** Shopify-SKU oder Sucheingabe → Herstellernummer (Großbuchstaben, ohne Präfix). */
  function baseSku(s) {
    const u = String(s || '').trim().toUpperCase();
    return u.startsWith(SKU_PREFIX) ? u.slice(SKU_PREFIX.length) : u;
  }

  // --- Verfügbarkeit ---------------------------------------------------------
  // Die Trefferliste entsteht im Browser, also kommen auch die Lieferhinweise
  // aus einer statischen Datei. Die Anzeige läuft absichtlich NACH dem Rendern:
  // Treffer und Preise sollen nicht darauf warten, und ohne die Datei fehlt
  // lediglich eine Zeile.
  const LAGER_TEXT = {
    lagernd: 'Sofort lieferbar',
    einzelstueck: 'Nur noch wenige verfügbar',
    nicht_lieferbar: 'Derzeit nicht lieferbar',
    erwartet: 'Wieder verfügbar in Kürze',
    lieferzeit: 'Lieferzeit ca. 7 Werktage',
  };
  const LAGER_KLASSE = {
    lagernd: 'ist-lagernd',
    einzelstueck: 'ist-knapp',
    nicht_lieferbar: 'ist-gesperrt',
    erwartet: 'ist-knapp',
    lieferzeit: '',
  };

  let stockPromise = null;
  function getStock() {
    if (!STOCK_CFG || !STOCK_CFG.url) return Promise.resolve(null);
    if (!stockPromise) stockPromise = fetchJson(STOCK_CFG.url).catch(() => null);
    return stockPromise;
  }

  /**
   * Lieferzustand einer Artikelnummer. Kein Eintrag heißt „keine Angabe" —
   * dann bleibt die Zeile leer, statt eine Lieferzeit zu versprechen, die
   * der Hersteller nie gemeldet hat.
   */
  function stockFor(daten, sku) {
    if (!daten) return null;
    const nr = daten.s && daten.s[sku];
    if (nr === undefined) return null;
    const code = daten.c ? daten.c[nr] : null;
    if (!code) return null;
    return {
      code,
      text: (daten.t && daten.t[sku]) || LAGER_TEXT[code] || '',
      klasse: LAGER_KLASSE[code] || '',
    };
  }

  const stockSlot = (sku) => `<span class="finder__item-lager" data-lager="${esc(sku)}"></span>`;

  /** Platzhalter in einem frisch gerenderten Bereich mit Text füllen. */
  async function hydrateStock(root) {
    const slots = [...root.querySelectorAll('[data-lager]')];
    if (slots.length === 0) return;
    const daten = await getStock();
    if (!daten) {
      slots.forEach((el) => el.removeAttribute('data-lager'));
      return;
    }
    for (const el of slots) {
      const z = stockFor(daten, el.dataset.lager);
      el.removeAttribute('data-lager');
      if (!z || !z.text) continue;
      el.className = `finder__item-lager ${z.klasse}`;
      el.textContent = z.text;
    }
  }

  // --- Daten-Layer ---
  const jsonCache = new Map();
  function fetchJson(url) {
    if (!jsonCache.has(url)) {
      jsonCache.set(
        url,
        fetch(url).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}: ${url}`);
          return r.json();
        }),
      );
    }
    return jsonCache.get(url);
  }
  const getManifest = () => fetchJson(CONFIG.manifestUrl);

  // --- Preise ---
  // Die Verkaufspreise kommen über JTL. Das Release kennt nur die Listenpreise
  // vom letzten Import; die Tagesdatei (#finder-prices) liegt darüber.
  let dayPricesPromise = null;
  function getDayPrices() {
    if (!PRICES_CFG || !PRICES_CFG.url) return Promise.resolve(null);
    if (!dayPricesPromise) dayPricesPromise = fetchJson(PRICES_CFG.url).catch(() => null);
    return dayPricesPromise;
  }

  /**
   * [Shop-Preis, UVP|null] einer Nummer — so, wie die Produktseite sie zeigt.
   * listCent ist der VK-Brutto der Herstellerliste und zugleich die UVP. Fehlt
   * die Nummer in der Tagesdatei (seit dem letzten Abgleich neu angelegt),
   * gilt der Listenpreis: Das ist auch ihr Startpreis im Shop. Streichpreis
   * nach derselben Regel wie der Preisabgleich — UVP nur, wenn der Shop-Preis
   * darunter liegt, und höchstens „m" % darunter (mehr gilt als Datenfehler);
   * liefert die Datei „u", gilt stattdessen der Stand bei Shopify.
   */
  function shopPrice(day, sku, listCent) {
    const cent = day && day.p && day.p[sku] !== undefined ? day.p[sku] : listCent;
    if (day && day.u) return [cent, day.u[sku] !== undefined ? day.u[sku] : null];
    const maxRabatt = day && day.m != null ? day.m : null;
    const mitUvp =
      listCent != null &&
      cent != null &&
      cent > 0 &&
      cent < listCent &&
      (maxRabatt == null || cent * 100 >= listCent * (100 - maxRabatt));
    return [cent, mitUvp ? listCent : null];
  }

  /** PKW: { sku: [cent, uvpCent|null] } */
  let pricesPromise = null;
  function getPrices() {
    if (!pricesPromise) {
      pricesPromise = Promise.all([getManifest().then((m) => fetchJson(m.prices)), getDayPrices()]).then(
        ([liste, tag]) => {
          const out = {};
          for (const sku of Object.keys(liste)) out[sku] = shopPrice(tag, sku, liste[sku][0]);
          return out;
        },
      );
    }
    return pricesPromise;
  }

  async function getVehChunks(makeSlug) {
    const m = await getManifest();
    const files = m.vehicles[makeSlug] || [];
    return Promise.all(files.map(fetchJson));
  }

  async function getFitments(tecId) {
    const m = await getManifest();
    const bucket = ((tecId % m.fit.buckets) + m.fit.buckets) % m.fit.buckets;
    const chunk = await fetchJson(m.fit.files[bucket]);
    return { tuples: chunk.v[String(tecId)] || [], zusatz: chunk.z, lines: m.lines };
  }

  // --- Daten-Layer Motorrad ---
  const getMotoManifest = () => fetchJson(CONFIG.moto.manifestUrl);

  /** Moto: { linien, skus: { sku: [cent, linieIdx, uvpCent|null] } } */
  let motoPricesPromise = null;
  function getMotoPrices() {
    if (!motoPricesPromise) {
      motoPricesPromise = Promise.all([getMotoManifest().then((m) => fetchJson(m.prices)), getDayPrices()]).then(
        ([liste, tag]) => {
          const skus = {};
          for (const [sku, e] of Object.entries(liste.skus)) {
            const [cent, uvp] = shopPrice(tag, sku, e[0]);
            skus[sku] = [cent, e[1], uvp];
          }
          return { linien: liste.linien, skus };
        },
      );
    }
    return motoPricesPromise;
  }

  async function getMotoVehChunks(makeSlug) {
    const m = await getMotoManifest();
    const files = m.vehicles[makeSlug] || [];
    return Promise.all(files.map(fetchJson));
  }

  // Moto-Fit-Tupel: [sku, gruppeIdx, seite(V|H|PB|''), pos(L|R|''), hinweisIdx]
  async function getMotoFitments(vehId) {
    const m = await getMotoManifest();
    const bucket = ((vehId % m.fit.buckets) + m.fit.buckets) % m.fit.buckets;
    const chunk = await fetchJson(m.fit.files[bucket]);
    return { tuples: chunk.v[String(vehId)] || [], hinweise: chunk.h, gruppen: m.gruppen };
  }

  async function hsnLookup(hsnTsn) {
    const m = await getManifest();
    const file = m.hsn.files[hsnTsn.slice(0, 3)] || m.hsn.files[hsnTsn.slice(0, 2)];
    if (!file) return [];
    const idx = await fetchJson(file);
    return idx[hsnTsn] || []; // [[tecId, makeSlug], …]
  }

  // --- Zustand ---
  function getState() {
    try {
      const s = JSON.parse(localStorage.getItem(LS_KEY));
      return s && s.vehicle && Array.isArray(s.skus) ? s : null;
    } catch {
      return null;
    }
  }
  function setVehicle(vehicle, skus) {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ release: CONFIG ? CONFIG.release : null, vehicle, skus, savedAt: new Date().toISOString() }),
    );
    document.dispatchEvent(new CustomEvent('finder:changed'));
  }
  function clearVehicle() {
    localStorage.removeItem(LS_KEY);
    document.dispatchEvent(new CustomEvent('finder:changed'));
  }

  // --- Zustand Motorrad (separat vom PKW-Fahrzeug) ---
  function getMotoState() {
    try {
      const s = JSON.parse(localStorage.getItem(LS_KEY_MOTO));
      return s && s.vehicle && Array.isArray(s.skus) ? s : null;
    } catch {
      return null;
    }
  }
  function setMotoVehicle(vehicle, skus) {
    localStorage.setItem(
      LS_KEY_MOTO,
      JSON.stringify({ release: CONFIG ? CONFIG.release : null, vehicle, skus, savedAt: new Date().toISOString() }),
    );
    document.dispatchEvent(new CustomEvent('finder:changed'));
  }
  function clearMotoVehicle() {
    localStorage.removeItem(LS_KEY_MOTO);
    document.dispatchEvent(new CustomEvent('finder:changed'));
  }

  // Bei neuem Daten-Release die gespeicherten SKU-Listen still revalidieren
  async function revalidate() {
    const s = getState();
    if (s && CONFIG && s.release !== CONFIG.release) {
      try {
        const { tuples } = await getFitments(s.vehicle.tecId);
        if (tuples.length === 0) {
          clearVehicle();
        } else {
          setVehicle(s.vehicle, tuples.map((t) => t[0]));
        }
      } catch {
        /* Netzfehler: alte Daten behalten */
      }
    }
    const ms = getMotoState();
    if (ms && HAS_MOTO && ms.release !== CONFIG.release) {
      try {
        const { tuples } = await getMotoFitments(ms.vehicle.vehId);
        if (tuples.length === 0) {
          clearMotoVehicle();
        } else {
          setMotoVehicle(ms.vehicle, tuples.map((t) => t[0]));
        }
      } catch {
        /* Netzfehler: alte Daten behalten */
      }
    }
  }

  // --- Formatierung ---
  const fmtEur = (cent) =>
    (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Veh-Tupel: [tecId, modelIdx, ausf, aufbauart, typ1, modellBj, fhzBj, kw, ps, ccm, motorcode, antrieb, hsn[]]
  function typeLabel(t) {
    const parts = [t[2]];
    if (t[3]) parts.push(t[3]);
    if (t[7] || t[8]) parts.push(`${t[7] ?? '?'} kW / ${t[8] ?? '?'} PS`);
    if (t[6]) parts.push(t[6]);
    if (t[10]) parts.push(t[10]);
    return parts.join(' · ');
  }
  function vehicleLabel(makeLabel, modelName, t) {
    // Modell weglassen, wenn die Ausführung damit beginnt ("B280" + "B280 (4-Kolben)")
    const model =
      modelName.length >= 2 && String(t[2]).toLowerCase().startsWith(modelName.toLowerCase())
        ? ''
        : modelName;
    const extra = [];
    if (t[7]) extra.push(`${t[7]} kW`);
    const bj = t[6] || t[5];
    if (bj) extra.push(bj);
    const base = `${makeLabel} ${model} ${t[2]}`.replace(/\s+/g, ' ').trim();
    return extra.length > 0 ? `${base} (${extra.join(', ')})` : base;
  }

  const isCaliperMake = (label) => label.includes('Bremssättel');

  const TYPE_ORDER = ['belag', 'scheibe', 'trommel', 'backe', 'handbremsbacke', 'vwk'];
  const TYPE_LABELS = {
    belag: 'Bremsbeläge',
    scheibe: 'Bremsscheiben',
    trommel: 'Bremstrommeln',
    backe: 'Bremsbacken',
    handbremsbacke: 'Handbremsbacken',
    vwk: 'Verschleißwarnkontakte',
  };
  const ACHSE_LABELS = { VA: 'Vorderachse', HA: 'Hinterachse', VAHA: 'Vorder- & Hinterachse', '': '' };

  // --- Ergebnis-Rendering ---
  // Wie auf der Produktseite: der Shop-Preis, dazu die UVP durchgestrichen,
  // wenn sie höher ist.
  function priceBlock(cent, uvpCent) {
    if (cent == null || Number.isNaN(cent)) return '';
    const uvp =
      uvpCent != null && uvpCent > cent
        ? `<span class="finder__price-uvp">UVP <s class="finder__price-old">${fmtEur(uvpCent)}</s></span>`
        : '';
    return `<span class="finder__price">
      ${uvp}
      <span class="finder__price-now">${fmtEur(cent)}</span>
    </span>`;
  }

  // Die EBC-Serien SIND Farben — als Punkt vor dem Seriennamen
  const LINE_COLORS = [
    ['blackstuff', '#26282c'],
    ['greenstuff', '#2e7d32'],
    ['redstuff', '#c62828'],
    ['yellowstuff', '#f2b705'],
    ['bluestuff', '#1565c0'],
    ['orangestuff', '#ef6c00'],
  ];
  function lineDot(line) {
    const name = (line.linie || '').toLowerCase();
    const hit = LINE_COLORS.find(([k]) => name.includes(k));
    return hit ? `<span class="finder__dot" style="background:${hit[1]}" aria-hidden="true"></span>` : '';
  }

  // Achsen-Icons (Betreiber-Entwürfe, 2026-08-23): eine Silhouette, das
  // betroffene Rad wird rot gefüllt. Front = LINKS (Haube fällt nach links ab).
  function axleIcon(achse) {
    const front = achse === 'VA' || achse === 'VAHA';
    const rear = achse === 'HA' || achse === 'VAHA';
    return `<svg class="finder__axle-icon" width="44" height="20" viewBox="0 0 332.06 153.46" aria-hidden="true">
      <path class="body" d="M327.06,70.07c-.47-5.62-.99-11.98-2.01-17.47-.71-3.84-2.27-8.95-7.54-8.95-.53,0-1.06.05-1.58.11-.33.03-.65.07-1.19.09-.01,0-.18-.17-.4-.8-1.55-4.43-8.04-9.95-16.37-16.69-2.05-1.65-4.51-3.65-5.55-4.68.38-2.66.33-5.92-1.52-8.17-.86-1.05-2.48-2.3-5.26-2.3-.41,0-.84.03-1.81.11-2.01,0-6.04-.83-11.61-1.98C257.84,6.39,231.07.89,190.94.02c-58.44-1.16-104.36,41.01-106.25,42.79l-1.35,1.26c-2.4,1.16-9.98,3.93-33.15,8.49-32.86,6.47-39.13,16.94-40.31,20.99-2.62,1.96-6.87,6.63-7.54,12.12-.54,4.38-1.77,6.17-1.77,6.17l-.57.73v30.92l.06.56c1.78,8.21,10.29,12.56,24.61,12.56,4.57,0,7.99-.47,7.86-.47.2-.01,1.97-.04,5.03-.1-.31-.87-.6-1.74-.84-2.64-.24-.86-.46-1.73-.62-2.62-.4-2.1-.63-4.26-.63-6.47,0-19,15.46-34.47,34.47-34.47s34.47,15.46,34.47,34.47c0,1.72-.17,3.39-.41,5.04-.13.9-.29,1.79-.49,2.66-.21.9-.42,1.79-.7,2.67,43.04-.92,97.8-2.14,140.22-3.21-.19-.87-.31-1.75-.43-2.64-.12-.88-.19-1.76-.23-2.65-.03-.62-.09-1.23-.09-1.86,0-19,15.46-34.47,34.46-34.47s34,15.04,34.42,33.68c0,.26.04.52.04.79,0,.67-.06,1.32-.1,1.98-.05.94-.14,1.88-.27,2.8,8.1-1.34,14.19-5.6,17.71-12.51,6.03-11.8,2.96-27.78.79-31.17-1.17-1.84-1.75-9.01-2.27-15.34Z"/>
      <path class="wheel${front ? ' on' : ''}" d="M43.18,135.92c4.49,10.32,14.77,17.55,26.75,17.55s22.97-7.76,27.19-18.68c.33-.87.63-1.76.89-2.67.24-.87.44-1.76.61-2.66.29-1.67.48-3.39.48-5.15,0-16.11-13.05-29.16-29.16-29.16s-29.16,13.06-29.16,29.16c0,2.18.26,4.31.72,6.36.19.89.43,1.77.71,2.62.29.9.62,1.77.99,2.63Z"/>
      <path class="wheel${rear ? ' on' : ''}" d="M248.44,131.32c3.15,12.71,14.6,22.15,28.28,22.15s26.18-10.31,28.66-23.89c.16-.88.28-1.77.37-2.67.08-.86.13-1.72.13-2.6v-.07c-.03-16.07-13.08-29.1-29.16-29.1s-29.16,13.05-29.16,29.16c0,.59.06,1.15.09,1.73.05.9.14,1.77.27,2.65.13.89.31,1.77.52,2.64Z"/>
    </svg>`;
  }

  // Motorrad-Icons (Betreiber-Entwürfe, 2026-08-23): eine Silhouette, das
  // betroffene Rad wird rot gefüllt. Front = RECHTS. Parkbremse/ohne Seite: kein Icon.
  function motoAxleIcon(seite) {
    const front = seite === 'V';
    const rear = seite === 'H';
    if (!front && !rear) return '';
    const w = (on) => `class="wheel${on ? ' on' : ''}"`;
    return `<svg class="finder__axle-icon finder__axle-icon--moto" width="36" height="21" viewBox="0 0 512 301.29" aria-hidden="true">
      <path class="body" d="M66.75,224.16c-2.61,1.89-3.71,5.25-2.72,8.32s3.85,5.14,7.08,5.14h89.74l1.37,4.46,1.4,4.54c2.61,8.48,10.44,14.26,19.31,14.26h58.86l98.53-73.27c.44-.56.94-1.08,1.49-1.54l26.98-22.92h-51.59v-4.54h56.93l8.25-7.01h-89.75v-4.54h94.97v.11l.64-.54,46.1,87.04c1.33,2.52,3.91,3.96,6.58,3.96,1.17,0,2.37-.28,3.48-.87,3.63-1.92,5.01-6.42,3.09-10.05l-45.83-86.53,5.01-.08,53.56-.88c2.23-.04,4.08-1.72,4.33-3.94l.93-8.38c.09-.83-.05-1.67-.42-2.42l-29.5-60.85L376.95,1c-.92-.98-2.36-1.27-3.59-.73l-8.38,3.72c-1.67.74-2.36,2.75-1.5,4.37l23.96,44.92-18.35,15.93-3.46,3-12.77-24.1c-.96-1.8-2.61-3.13-4.57-3.68l-24.24-6.76c-3.95-1.11-8.06,1.21-9.16,5.16-1.1,3.96,1.21,8.06,5.17,9.16l21.16,5.9,10,18.87h-32.91l-59.17-15.46c-4.39-1.15-9.06-.17-12.62,2.63l-47.08,37.07c-4.8,3.78-10.68,5.92-16.79,6.12l-51.17,1.63c-5.94.19-11.6-2.52-15.15-7.28-2.33-3.13-5.18-6.77-7.98-10.29-7.24-9.09-18.29-14.31-29.91-14.09l-60.57,1.11c-1.79.03-3.25,1.45-3.34,3.23l-.49,9.75c-.06,1.29.61,2.5,1.73,3.14l122.17,68.86.99,3.23,1.39,4.51-73.57,53.23ZM427.46,69.63l25.36,52.98s-24.89-13.34-32.31-28.93c-7.42-15.59,6.95-24.05,6.95-24.05ZM94.07,222.75l50.87-36.81,1.39,4.51,8.57,27.84,1.37,4.46h-62.2Z"/>
      <path class="body" d="M300.21,260.89c1,0,1.97-.38,2.7-1.07l31.65-29.91c.91-.86,1.45-2.03,1.52-3.27l1.59-27.78-83.42,62.03h45.97Z"/>
      <path ${w(rear)} d="M112.92,214.62c.45,1.2.84,2.42,1.18,3.67h27.1c-1.16-6.91-3.32-13.48-6.32-19.56l-21.96,15.89Z"/>
      <path ${w(rear)} d="M71.1,274.81c-24.6,0-44.62-20.02-44.62-44.62s20.02-44.62,44.62-44.62c10.52,0,20.2,3.67,27.84,9.79l21.97-15.9c-12.83-12.6-30.41-20.38-49.81-20.38C31.83,159.08,0,190.92,0,230.19s31.83,71.11,71.1,71.11c35.21,0,64.43-25.6,70.1-59.21h-27.1c-5.22,18.84-22.51,32.72-43,32.72h0Z"/>
      <path ${w(front)} d="M440.9,185.57c24.6,0,44.62,20.02,44.62,44.62s-20.02,44.62-44.62,44.62-44.62-20.02-44.62-44.62c0-12.75,5.39-24.26,13.99-32.4l-12.69-23.96c-16.89,13-27.78,33.41-27.78,56.37,0,39.27,31.83,71.11,71.1,71.11s71.11-31.84,71.11-71.11-31.83-71.1-71.11-71.1c-7.79,0-15.28,1.27-22.3,3.59l12.69,23.95c3.1-.68,6.31-1.06,9.61-1.06h0Z"/>
    </svg>`;
  }

  // Betreiber-Icon (Flächen-Icon, beschnittene viewBox) — identisch zu snippets/icon-car.liquid
  function carIcon() {
    return `<svg class="ebc-icon-car" viewBox="10 148 492 216" fill="currentColor" aria-hidden="true"><path d="m357.13 218.74c-7.76-12.74-43.94-65.59-110.13-65.59h-82.34c-46.95 0-70 51.4-75.44 65.58h-3.59v-1.52a21.11 21.11 0 0 0 -17.52-20.83l-47.34-8.21a5.28 5.28 0 0 0 -4.15 1.12 5.09 5.09 0 0 0 -1.81 3.9v29.49a5.72 5.72 0 0 0 .11.57 5.72 5.72 0 0 0 -.11.57v63.83a44.6 44.6 0 0 0 44.55 44.55h3.39a49.38 49.38 0 0 0 87.68 0h203.42a49.39 49.39 0 0 0 87.69 0h41.1a14.57 14.57 0 0 0 14.55-14.56v-45.13c0-39.09-71.79-53.37-140.06-53.77zm129.47 50.61a18.46 18.46 0 0 1 .41 3.16v16.32c-7.11-2.06-18.69-5.81-26.52-10.4-5-2.94-6.85-6.4-7.49-9 15.08-.16 30.4-.21 33.11 0 .16 0 .33-.1.49-.08zm-461.6-70.11 41.38 7.18a10.93 10.93 0 0 1 9.08 10.79l-50.46.38zm81.6 149.42a39.66 39.66 0 1 1 39.65-39.66 39.71 39.71 0 0 1 -39.66 39.66zm291.1 0a39.66 39.66 0 1 1 39.65-39.66 39.71 39.71 0 0 1 -39.66 39.66zm84.94-26.66h-37.05a49.13 49.13 0 0 0 1.94-13 49.85 49.85 0 1 0 -97.74 13h-195.3a49.13 49.13 0 0 0 1.94-13 49.85 49.85 0 1 0 -97.76 13 34.37 34.37 0 0 1 -33.67-34.35v-58.73h67.8a5.11 5.11 0 0 0 4.83-3.5c.21-.62 21.12-62.08 67-62.08h82.37c67.46 0 102.35 62.29 102.69 62.92a5.12 5.12 0 0 0 4.47 2.66c65 0 112.46 11.73 127.62 30.26-6.15-.19-16.41-.08-34 .13a5.09 5.09 0 0 0 -4.89 3.91c-.15.6-3.32 14.75 12.44 24 10 5.86 24.73 10.31 31.67 12.22v18.21a4.38 4.38 0 0 1 -4.36 4.35z"/><path d="m171.31 237.59h-21a5.09 5.09 0 0 0 0 10.18h21a5.09 5.09 0 0 0 0-10.18z"/><path d="m397.69 281.54a27.47 27.47 0 1 0 27.47 27.46 27.5 27.5 0 0 0 -27.47-27.46zm0 44.75a17.29 17.29 0 1 1 17.31-17.29 17.3 17.3 0 0 1 -17.31 17.29z"/><path d="m106.59 281.54a27.47 27.47 0 1 0 27.47 27.46 27.5 27.5 0 0 0 -27.47-27.46zm0 44.75a17.29 17.29 0 1 1 17.28-17.29 17.3 17.3 0 0 1 -17.28 17.29z"/><path d="m244.57 174.18h-74.51a19.43 19.43 0 0 0 -19.4 19.37l-.46 29a5.1 5.1 0 0 0 5.09 5.17h151.71a11.25 11.25 0 0 0 9.66-17c-16.02-26.94-48.85-36.54-72.09-36.54zm62.43 43.41h-146.54l.38-23.92v-.08a9.23 9.23 0 0 1 9.22-9.22h74.51c20.57 0 49.5 8.28 63.34 31.65z"/></svg>`;
  }

  function wireChips(container) {
    const bar = container.querySelector('[data-finder-chips]');
    if (!bar) return;
    bar.addEventListener('click', (e) => {
      const btn = e.target.closest('.finder__chip');
      if (!btn) return;
      bar.querySelectorAll('.finder__chip').forEach((b) => b.classList.toggle('is-active', b === btn));
      const typ = btn.dataset.typ;
      // Im Modal: Höhenwechsel des Panels weich animieren (FLIP), statt zu springen
      const panel = container.closest('.ebc-modal__panel');
      const h0 = panel ? panel.offsetHeight : 0;
      container.querySelectorAll('.finder__group').forEach((g) => {
        g.hidden = typ !== 'alle' && g.dataset.typ !== typ;
      });
      if (panel) animatePanelHeight(panel, h0);
    });
  }
  // FLIP für Modal-Panels: Starthöhe festnageln, Reflow, auf Zielhöhe übergehen,
  // danach wieder freigeben (Transition + reduced-motion in ebc-base.css).
  function animatePanelHeight(panel, h0) {
    const h1 = panel.offsetHeight;
    if (!h0 || h0 === h1) return;
    if (panel._resizeTimer) clearTimeout(panel._resizeTimer);
    panel.style.transition = 'none';
    panel.style.height = `${h0}px`;
    panel.classList.add('is-resizing');
    void panel.offsetHeight; // Reflow
    panel.style.transition = '';
    panel.style.height = `${h1}px`;
    const done = () => {
      panel.style.height = '';
      panel.classList.remove('is-resizing');
      panel._resizeTimer = null;
    };
    panel._resizeTimer = setTimeout(done, 420);
  }

  // Einheitliches Ergebnis-Item (Finder + Collection-Finder)
  // Serienbild-URLs der Map (ebc-line-images.liquid) sind _240x240-CDN-Varianten —
  // im Kartenraster der PDP-Empfehlungen (Bild ~240px breit, Retina) die
  // _480x480-Variante anfordern; die Listen-Optik (8rem) bleibt bei 240.
  function lineImgSrc(url, big) {
    return big ? url.replace(/_240x240\.(jpe?g|png|webp)/i, '_480x480.$1') : url;
  }

  function resultItemHtml(item, prices, opts = {}) {
    const p = prices[item.sku];
    const r90 = item.line.r90 === true ? '<span class="finder__badge">ECE R90</span>' : '';
    // Serienbild (zentral gepflegt) hat Vorrang vor dem Produktbild
    const lineImg = getLineImages()[slugify(item.line.label)];
    const imgSlot = lineImg
      ? `<span class="finder__item-img" aria-hidden="true"><img src="${esc(lineImgSrc(lineImg, opts.cards))}" alt="" loading="lazy" width="${opts.cards ? 240 : 60}" height="${opts.cards ? 240 : 60}"></span>`
      : `<span class="finder__item-img" data-img aria-hidden="true"></span>`;
    return `<li class="finder__item">
      <a class="finder__item-link" href="${productUrl(item.sku)}" data-handle="ebc-${slugify(item.sku)}">
        ${imgSlot}
        <span class="finder__item-main">
          <span class="finder__item-line">${lineDot(item.line)}${esc(item.line.label)} ${r90}</span>
          <span class="finder__item-sku">Art.-Nr. ${esc(shopSku(item.sku))}</span>
          <span class="finder__item-zusatz">${esc(item.zusatz)}</span>
          ${stockSlot(item.sku)}
        </span>
        ${p ? priceBlock(p[0], p[1]) : ''}
      </a>
    </li>`;
  }

  async function renderResults(container, vehicle, opts = {}) {
    container.innerHTML = `<div class="finder__skeleton">${'<div class="finder__skeleton-row"></div>'.repeat(3)}</div>`;
    let loaded;
    try {
      loaded = await Promise.all([getFitments(vehicle.tecId), getPrices()]);
    } catch {
      container.innerHTML = '<p class="finder__error">Die Daten konnten nicht geladen werden — bitte versuche es gleich noch einmal.</p>';
      return;
    }
    const [{ tuples, zusatz, lines }, prices] = loaded;
    if (tuples.length === 0) {
      container.innerHTML = '<p>Für dieses Fahrzeug wurden keine Produkte gefunden.</p>';
      return;
    }
    setVehicle(vehicle, tuples.map((t) => t[0]));

    // Gruppieren: Produkttyp → Achse → Einträge
    const groups = new Map();
    for (const [sku, achse, zIdx, lineIdx] of tuples) {
      const line = lines[lineIdx];
      const key = `${line.typ}|${achse}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ sku, achse, zusatz: zusatz[zIdx], line });
    }
    const sortedKeys = [...groups.keys()].sort((a, b) => {
      const [ta, aa] = a.split('|');
      const [tb, ab] = b.split('|');
      const to = TYPE_ORDER.indexOf(ta) - TYPE_ORDER.indexOf(tb);
      return to !== 0 ? to : aa.localeCompare(ab);
    });

    const typCounts = new Map();
    for (const key of sortedKeys) {
      const [typ] = key.split('|');
      typCounts.set(typ, (typCounts.get(typ) ?? 0) + groups.get(key).length);
    }

    let html = `
      <div class="finder__vehicle-bar">
        ${carIcon()}
        <div>
          <div class="finder__vehicle-label">${esc(vehicle.label)}</div>
          <div class="finder__vehicle-meta">${tuples.length} passende Produkte${vehicle.hsnTsn ? ` · HSN/TSN ${esc(vehicle.hsnTsn)}` : ''}</div>
        </div>
        <span class="finder__vehicle-saved">✓ Fahrzeug gemerkt</span>
      </div>
      <div class="finder__chips" data-finder-chips>
        <button type="button" class="finder__chip is-active" data-typ="alle">Alle <span class="finder__chip-count">${tuples.length}</span></button>
        ${[...typCounts.entries()]
          .map(
            ([typ, c]) =>
              `<button type="button" class="finder__chip" data-typ="${esc(typ)}">${esc(TYPE_LABELS[typ] || typ)} <span class="finder__chip-count">${c}</span></button>`,
          )
          .join('')}
      </div>`;

    for (const key of sortedKeys) {
      const [typ, achse] = key.split('|');
      const items = groups.get(key);
      html += `<section class="finder__group" data-typ="${esc(typ)}">
        <h3 class="finder__group-title">${achse ? axleIcon(achse) : ''}${esc(TYPE_LABELS[typ] || typ)}${achse ? ` — ${esc(ACHSE_LABELS[achse] ?? achse)}` : ''} <span class="finder__group-count">(${items.length})</span></h3>
        <ul class="finder__items" role="list">`;
      for (const item of items) {
        html += resultItemHtml(item, prices);
      }
      html += '</ul></section>';
    }
    container.innerHTML = html;
    wireChips(container);
    hydrateImages(container);
    appendUniversal(container);
    if (opts.scroll !== false) container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Universal-Zubehör (Collection finder-universal) an jedes Ergebnis anhängen —
  // Bremsflüssigkeit & Co. passen für alle Fahrzeuge (wie im Alt-Shop).
  async function appendUniversal(container) {
    try {
      const res = await fetch('/collections/finder-universal/products.json?limit=12');
      if (!res.ok) return;
      const { products } = await res.json();
      if (!products || products.length === 0) return;
      let html = `<section class="finder__group" data-typ="zubehoer">
        <h3 class="finder__group-title">Zubehör <span class="finder__group-count">— passt für alle Fahrzeuge</span></h3>
        <ul class="finder__items" role="list">`;
      for (const p of products) {
        const v = p.variants && p.variants[0];
        const cent = v ? Math.round(parseFloat(v.price) * 100) : null;
        const uvp = v && v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null;
        const img = p.images && p.images[0] ? p.images[0].src : null;
        const imgHtml = img
          ? `<img src="${esc(img)}${img.includes('?') ? '&' : '?'}width=120" alt="" loading="lazy" width="60" height="60">`
          : '';
        html += `<li class="finder__item">
          <a class="finder__item-link" href="/products/${esc(p.handle)}">
            <span class="finder__item-img" aria-hidden="true">${imgHtml}</span>
            <span class="finder__item-main">
              <span class="finder__item-line">${esc(p.title)}</span>
              ${v && v.sku ? `<span class="finder__item-sku">Art.-Nr. ${esc(shopSku(baseSku(v.sku)))}</span>${stockSlot(baseSku(v.sku))}` : ''}
            </span>
            ${priceBlock(cent, uvp)}
          </a>
        </li>`;
      }
      html += '</ul></section>';
      container.insertAdjacentHTML('beforeend', html);
      hydrateStock(container);
      const bar = container.querySelector('[data-finder-chips]');
      if (bar) {
        bar.insertAdjacentHTML(
          'beforeend',
          `<button type="button" class="finder__chip" data-typ="zubehoer">Zubehör <span class="finder__chip-count">${products.length}</span></button>`,
        );
      }
    } catch {
      /* Zubehör optional */
    }
  }

  // --- Motorrad: Kategorien, Labels, Ergebnis-Rendering ---
  // Kategorie → [Label, KatalogCode, Gruppen-Filter]; Kaskade wie im Alt-Shop:
  // Artikelauswahl → Marke → Modell (Beläge & Scheiben teilen den Katalog "bremse").
  const MOTO_KATEGORIEN = [
    ['belaege', 'Bremsbeläge', 0],
    ['scheiben', 'Bremsscheiben', 0],
    ['kupplung', 'Kupplungen', 1],
    ['stahlflex', 'Stahlflex-Bremsleitungen', 2],
  ];
  const MOTO_KAT_LABELS = Object.fromEntries(MOTO_KATEGORIEN.map(([k, l]) => [k, l]));
  function motoGruppeKategorie(gruppeKey) {
    if (gruppeKey.indexOf('belag-') === 0) return 'belaege';
    if (gruppeKey.indexOf('disc') === 0 || gruppeKey === 'bolzen') return 'scheiben';
    if (gruppeKey.indexOf('kupplung') === 0) return 'kupplung';
    if (gruppeKey === 'stahlflex') return 'stahlflex';
    return 'zubehoer';
  }
  const MOTO_SEITE_LABELS = { V: 'vorne', H: 'hinten', PB: 'Parkbremse', '': '' };
  const MOTO_POS_LABELS = { L: 'links', R: 'rechts', '': '' };

  function bikeIcon() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="5.5" cy="16.5" r="3.2"/><circle cx="18.5" cy="16.5" r="3.2"/><path d="M5.5 16.5 9 9.5h5.2M9 9.5H6.8M13.5 6.5h2.2l2.8 10M12 16.5h-2l4-7"/></svg>`;
  }

  function motoVehicleLabel(makeLabel, modelName, row) {
    // Row: [vehId, modelIdx, katalogCode, ausf, bem, ccm, bjVon, bjBis, bjRaw]
    const ausf = row[3] && !modelName.toLowerCase().includes(String(row[3]).toLowerCase()) ? row[3] : '';
    const base = `${makeLabel} ${modelName} ${ausf}`.replace(/\s+/g, ' ').trim();
    return row[8] ? `${base} (${row[8]})` : base;
  }

  // Moto-Ergebnis-Item — nutzt dieselbe Item-Optik wie der PKW-Finder
  function motoResultItemHtml(item, prices, opts = {}) {
    const p = prices.skus[item.sku];
    const linie = p ? prices.linien[p[1]] : null;
    const label = linie ? linie[0] : item.sku;
    const serie = linie ? linie[1] : '';
    const lineImg = getLineImages()['motorrad-' + serie];
    const imgSlot = lineImg
      ? `<span class="finder__item-img" aria-hidden="true"><img src="${esc(lineImgSrc(lineImg, opts.cards))}" alt="" loading="lazy" width="${opts.cards ? 240 : 60}" height="${opts.cards ? 240 : 60}"></span>`
      : `<span class="finder__item-img" data-img aria-hidden="true"></span>`;
    const einbau = [MOTO_SEITE_LABELS[item.seite], MOTO_POS_LABELS[item.pos]].filter(Boolean).join(' ');
    const sub = [einbau ? `Einbau: ${einbau}` : '', item.hinweis].filter(Boolean).join(' · ');
    return `<li class="finder__item">
      <a class="finder__item-link" href="${productUrl(item.sku)}" data-handle="ebc-${slugify(item.sku)}">
        ${imgSlot}
        <span class="finder__item-main">
          <span class="finder__item-line">${lineDot({ linie: serie })}${esc(label)}</span>
          <span class="finder__item-sku">Art.-Nr. ${esc(shopSku(item.sku))}</span>
          ${sub ? `<span class="finder__item-zusatz">${esc(sub)}</span>` : ''}
          ${stockSlot(item.sku)}
        </span>
        ${p ? priceBlock(p[0], p[2]) : ''}
      </a>
    </li>`;
  }

  // Links/Rechts mit identischer SKU zu einer Zeile zusammenfassen
  function collapseMotoItems(items) {
    const out = [];
    const seen = new Map();
    for (const it of items) {
      const key = `${it.sku}|${it.gruppeKey}|${it.seite}`;
      const prev = seen.get(key);
      if (prev && prev.pos !== it.pos && prev.pos !== '' && it.pos !== '') {
        prev.pos = '';
        continue;
      }
      const copy = { ...it };
      seen.set(key, copy);
      out.push(copy);
    }
    return out;
  }

  async function renderMotoResults(container, vehicle, opts = {}) {
    container.innerHTML = `<div class="finder__skeleton">${'<div class="finder__skeleton-row"></div>'.repeat(3)}</div>`;
    let loaded;
    try {
      loaded = await Promise.all([getMotoFitments(vehicle.vehId), getMotoPrices()]);
    } catch {
      container.innerHTML = '<p class="finder__error">Die Daten konnten nicht geladen werden — bitte versuchen Sie es gleich noch einmal.</p>';
      return;
    }
    const [{ tuples, hinweise, gruppen }, prices] = loaded;
    if (tuples.length === 0) {
      container.innerHTML = '<p>Für dieses Fahrzeug wurden keine Produkte gefunden.</p>';
      return;
    }
    setMotoVehicle(vehicle, tuples.map((t) => t[0]));

    const items = collapseMotoItems(
      tuples.map(([sku, gIdx, seite, pos, hIdx]) => {
        const g = gruppen[gIdx] || ['', ''];
        return { sku, gruppeKey: g[0], gruppeLabel: g[1], seite, pos, hinweis: hinweise[hIdx] || '' };
      }),
    );

    // Gruppieren: Kategorie → Seite → Gruppe
    const KAT_ORDER = ['belaege', 'scheiben', 'kupplung', 'stahlflex', 'zubehoer'];
    const SEITE_ORDER = ['V', 'H', 'PB', ''];
    const groups = new Map();
    for (const it of items) {
      const kat = motoGruppeKategorie(it.gruppeKey);
      const key = `${kat}|${it.seite}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    const sortedKeys = [...groups.keys()].sort((a, b) => {
      const [ka, sa] = a.split('|');
      const [kb, sb] = b.split('|');
      const ko = KAT_ORDER.indexOf(ka) - KAT_ORDER.indexOf(kb);
      return ko !== 0 ? ko : SEITE_ORDER.indexOf(sa) - SEITE_ORDER.indexOf(sb);
    });
    const katCounts = new Map();
    for (const key of sortedKeys) {
      const [kat] = key.split('|');
      katCounts.set(kat, (katCounts.get(kat) ?? 0) + groups.get(key).length);
    }

    let html = `
      <div class="finder__vehicle-bar">
        ${bikeIcon()}
        <div>
          <div class="finder__vehicle-label">${esc(vehicle.label)}</div>
          <div class="finder__vehicle-meta">${items.length} passende Produkte · Motorrad</div>
        </div>
        <span class="finder__vehicle-saved">✓ Fahrzeug gemerkt</span>
      </div>
      <div class="finder__chips" data-finder-chips>
        <button type="button" class="finder__chip is-active" data-typ="alle">Alle <span class="finder__chip-count">${items.length}</span></button>
        ${[...katCounts.entries()]
          .map(
            ([kat, c]) =>
              `<button type="button" class="finder__chip" data-typ="${esc(kat)}">${esc(MOTO_KAT_LABELS[kat] || 'Zubehör')} <span class="finder__chip-count">${c}</span></button>`,
          )
          .join('')}
      </div>`;

    for (const key of sortedKeys) {
      const [kat, seite] = key.split('|');
      const list = groups.get(key);
      // Innerhalb der Sektion nach Gruppe sortieren, Gruppenlabel als Zusatz
      list.sort((a, b) => a.gruppeLabel.localeCompare(b.gruppeLabel) || a.sku.localeCompare(b.sku));
      const seiteLabel = MOTO_SEITE_LABELS[seite];
      html += `<section class="finder__group" data-typ="${esc(kat)}">
        <h3 class="finder__group-title">${motoAxleIcon(seite)}${esc(MOTO_KAT_LABELS[kat] || 'Zubehör')}${seiteLabel ? ` — ${esc(seiteLabel)}` : ''} <span class="finder__group-count">(${list.length})</span></h3>
        <ul class="finder__items" role="list">`;
      for (const item of list) {
        html += motoResultItemHtml(item, prices);
      }
      html += '</ul></section>';
    }
    container.innerHTML = html;
    wireChips(container);
    hydrateImages(container);
    if (opts.scroll !== false) container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Zentral gepflegte Serienbilder (Metaobject produktlinie, von Liquid als Map gerendert)
  let lineImagesCache = null;
  function getLineImages() {
    if (lineImagesCache === null) {
      const el = document.querySelector('[data-line-images]');
      try {
        lineImagesCache = el ? JSON.parse(el.textContent) : {};
      } catch {
        lineImagesCache = {};
      }
    }
    return lineImagesCache;
  }

  // Produktbilder nachladen (nur für Items OHNE Serienbild; Browser cached)
  function hydrateImages(container) {
    hydrateStock(container);
    container.querySelectorAll('.finder__item-link[data-handle]').forEach(async (a) => {
      const slot = a.querySelector('[data-img]');
      if (!slot) return;
      try {
        const res = await fetch(`/products/${a.dataset.handle}.js`);
        if (!res.ok) return;
        const product = await res.json();
        const img = product.featured_image;
        if (!img) return;
        const sep = img.includes('?') ? '&' : '?';
        // Kartenraster (PDP-Empfehlungen, ab 750px): größere Variante fürs ~240px-Bild
        const big = !!slot.closest('.finder__items--cards') && window.matchMedia('(min-width: 750px)').matches;
        slot.innerHTML = `<img src="${esc(img)}${sep}width=${big ? 480 : 120}" alt="" loading="lazy" width="${big ? 240 : 60}" height="${big ? 240 : 60}">`;
      } catch {
        /* Bild optional */
      }
    });
  }

  // Optionen eines Selects setzen — inkl. strukturierter Daten für EbcSelect
  // (options: [{value, title, sub?, right?, group?}]). Dispatcht 'ebc:options'.
  function setSelectOptions(sel, placeholder, options) {
    sel._ebcOptions = options;
    let html = `<option value="">${esc(placeholder)}</option>`;
    let currentGroup = null;
    let groupOpen = false;
    for (const o of options) {
      const g = o.group ?? null;
      if (g !== currentGroup) {
        if (groupOpen) html += '</optgroup>';
        groupOpen = false;
        if (g) {
          html += `<optgroup label="${esc(g)}">`;
          groupOpen = true;
        }
        currentGroup = g;
      }
      const extra = [o.right, o.sub].filter(Boolean).join(' · ');
      html += `<option value="${esc(String(o.value))}">${esc(o.title)}${extra ? ` · ${esc(extra)}` : ''}</option>`;
    }
    if (groupOpen) html += '</optgroup>';
    sel.innerHTML = html;
    sel.dispatchEvent(new CustomEvent('ebc:options'));
  }
  const focusSelect = (sel, autoOpen) => {
    if (sel._ebcButton) {
      sel._ebcButton.focus();
      if (autoOpen && sel._ebcOpen) sel._ebcOpen();
    } else {
      sel.focus();
    }
  };

  // --- Kaskade (Marke→Modell→Typ bzw. Hersteller→Serie→Sattel) ---
  // Platzhalter-Texte kommen aus den in Liquid gerenderten ersten <option>s.
  class Cascade {
    // makes: Array [[slug,label],…] ODER {groups:[{label, items:[[slug,label],…]},…]} (optgroups)
    constructor(panel, makes, makeLabels, onVehicle) {
      this.makeLabels = makeLabels;
      this.onVehicle = onVehicle;
      this.steps = [...panel.querySelectorAll('[data-step]')];
      this.selMake = panel.querySelector('[data-finder-make]');
      this.selModel = panel.querySelector('[data-finder-model]');
      this.selType = panel.querySelector('[data-finder-type]');
      this.phMake = this.selMake.options[0].textContent;
      this.phModel = this.selModel.options[0].textContent;
      this.phType = this.selType.options[0].textContent;
      let makeOptions;
      if (Array.isArray(makes)) {
        makeOptions = makes.map(([slug, label]) => ({ value: slug, title: label }));
      } else {
        makeOptions = makes.groups
          .filter((g) => g.items.length > 0)
          .flatMap((g) => g.items.map(([slug, label]) => ({ value: slug, title: label, group: g.label })));
      }
      setSelectOptions(this.selMake, this.phMake, makeOptions);
      this.selMake.addEventListener('change', () => this.onMake());
      this.selModel.addEventListener('change', () => this.onModel());
      this.selType.addEventListener('change', () => this.onType());
    }

    setDone(index, done) {
      const step = this.steps[index];
      if (step) step.classList.toggle('is-done', done);
    }

    async onMake() {
      const slug = this.selMake.value;
      setSelectOptions(this.selModel, this.phModel, []);
      setSelectOptions(this.selType, this.phType, []);
      this.selModel.disabled = this.selType.disabled = true;
      this.setDone(0, !!slug);
      this.setDone(1, false);
      this.setDone(2, false);
      if (!slug) return;
      this.chunks = await getVehChunks(slug);
      const models = [];
      for (const c of this.chunks) for (const m of c.models) if (!models.includes(m)) models.push(m);
      setSelectOptions(this.selModel, this.phModel, models.map((m) => ({ value: m, title: m })));
      this.selModel.disabled = false;
      focusSelect(this.selModel, true);
    }

    onModel() {
      const model = this.selModel.value;
      setSelectOptions(this.selType, this.phType, []);
      this.selType.disabled = true;
      this.setDone(1, !!model);
      this.setDone(2, false);
      if (!model) return;
      this.currentTypes = [];
      for (const c of this.chunks) {
        for (const t of c.types) {
          if (c.models[t[1]] === model) this.currentTypes.push(t);
        }
      }
      setSelectOptions(
        this.selType,
        this.phType,
        this.currentTypes.map((t) => ({
          value: t[0],
          title: `${t[2]}${t[3] ? ` · ${t[3]}` : ''}`,
          right: t[7] || t[8] ? `${t[7] ?? '?'} kW / ${t[8] ?? '?'} PS` : '',
          sub: [t[6], t[10]].filter(Boolean).join(' · '),
        })),
      );
      this.selType.disabled = false;
      focusSelect(this.selType, true);
    }

    onType() {
      const tecId = parseInt(this.selType.value, 10);
      this.setDone(2, !!tecId);
      if (!tecId) return;
      const t = this.currentTypes.find((x) => x[0] === tecId);
      const vehicle = {
        tecId,
        make: this.selMake.value,
        label: vehicleLabel(this.makeLabels.get(this.selMake.value), this.selModel.value, t),
        hsnTsn: t[12] && t[12].length > 0 ? t[12][0] : null,
      };
      this.onVehicle(vehicle);
    }
  }

  // --- Motorrad-Kaskade (Artikelauswahl → Marke → Modell → Ausführung/Baujahr) ---
  class MotoCascade {
    constructor(panel, onVehicle) {
      this.onVehicle = onVehicle;
      this.steps = [...panel.querySelectorAll('[data-step]')];
      this.selKat = panel.querySelector('[data-moto-kat]');
      this.selMake = panel.querySelector('[data-moto-make]');
      this.selModel = panel.querySelector('[data-moto-model]');
      this.selRow = panel.querySelector('[data-moto-row]');
      this.phKat = this.selKat.options[0].textContent;
      this.phMake = this.selMake.options[0].textContent;
      this.phModel = this.selModel.options[0].textContent;
      this.phRow = this.selRow.options[0].textContent;
      this.makeLabels = new Map(CONFIG.moto.makes.map(([slug, label]) => [slug, label]));
      setSelectOptions(
        this.selKat,
        this.phKat,
        MOTO_KATEGORIEN.map(([key, label]) => ({ value: key, title: label })),
      );
      this.selKat.addEventListener('change', () => this.onKat());
      this.selMake.addEventListener('change', () => this.onMake());
      this.selModel.addEventListener('change', () => this.onModel());
      this.selRow.addEventListener('change', () => this.onRow());
    }

    setDone(index, done) {
      const step = this.steps[index];
      if (step) step.classList.toggle('is-done', done);
    }

    reset(fromStep) {
      if (fromStep <= 1) {
        setSelectOptions(this.selMake, this.phMake, []);
        this.selMake.disabled = true;
      }
      if (fromStep <= 2) {
        setSelectOptions(this.selModel, this.phModel, []);
        this.selModel.disabled = true;
      }
      setSelectOptions(this.selRow, this.phRow, []);
      this.selRow.disabled = true;
      for (let i = fromStep; i < 4; i++) this.setDone(i, false);
    }

    onKat() {
      const kat = this.selKat.value;
      this.reset(1);
      this.setDone(0, !!kat);
      if (!kat) return;
      const def = MOTO_KATEGORIEN.find(([k]) => k === kat);
      this.katalogCode = def[2];
      this.kategorie = kat;
      const bit = 1 << this.katalogCode;
      const makes = CONFIG.moto.makes.filter(([, , mask]) => (mask & bit) !== 0);
      setSelectOptions(this.selMake, this.phMake, makes.map(([slug, label]) => ({ value: slug, title: label })));
      this.selMake.disabled = false;
      focusSelect(this.selMake, true);
    }

    async onMake() {
      const slug = this.selMake.value;
      this.reset(2);
      this.setDone(1, !!slug);
      if (!slug) return;
      const chunks = await getMotoVehChunks(slug);
      // Nur Zeilen des gewählten Katalogs; Modell-Liste daraus ableiten
      this.rows = [];
      const models = [];
      for (const c of chunks) {
        for (const r of c.rows) {
          if (r[2] !== this.katalogCode) continue;
          const model = c.models[r[1]];
          this.rows.push({ model, r });
          if (!models.includes(model)) models.push(model);
        }
      }
      setSelectOptions(this.selModel, this.phModel, models.map((m) => ({ value: m, title: m })));
      this.selModel.disabled = false;
      focusSelect(this.selModel, true);
    }

    onModel() {
      const model = this.selModel.value;
      setSelectOptions(this.selRow, this.phRow, []);
      this.selRow.disabled = true;
      this.setDone(2, !!model);
      this.setDone(3, false);
      if (!model) return;
      this.currentRows = this.rows.filter((x) => x.model === model).map((x) => x.r);
      // Row: [vehId, modelIdx, kat, ausf, bem, ccm, bjVon, bjBis, bjRaw]
      setSelectOptions(
        this.selRow,
        this.phRow,
        this.currentRows.map((r) => ({
          value: r[0],
          title: r[3] || model,
          right: r[8] || '',
          sub: [r[5] ? `${r[5]} ccm` : '', r[4]].filter(Boolean).join(' · '),
        })),
      );
      this.selRow.disabled = false;
      if (this.currentRows.length === 1) {
        this.selRow.value = String(this.currentRows[0][0]);
        this.selRow.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        focusSelect(this.selRow, true);
      }
    }

    onRow() {
      const vehId = parseInt(this.selRow.value, 10);
      this.setDone(3, !!vehId);
      if (!vehId) return;
      const r = this.currentRows.find((x) => x[0] === vehId);
      const vehicle = {
        vehId,
        make: this.selMake.value,
        katalog: this.katalogCode,
        label: motoVehicleLabel(this.makeLabels.get(this.selMake.value), this.selModel.value, r),
      };
      this.onVehicle(vehicle, this.kategorie);
    }
  }

  // --- Segmented Control: gleitende Pille hinter dem aktiven Segment ---
  // Misst das aktive Segment und schreibt --thumb-x/-y/-w/-h auf den Container;
  // die Bewegung macht CSS (.finder__areas::before). Beobachtet wird die
  // is-active-Klasse, damit jeder Aufrufer — Klick, Wiederherstellung aus
  // localStorage, data-area der Section — die Pille automatisch mitnimmt.
  class SegmentedThumb {
    constructor(bar, itemSelector) {
      this.bar = bar;
      this.sel = itemSelector;
      this.ready = false;
      this.queued = false;
      this.sync = this.sync.bind(this);
      new MutationObserver(this.sync).observe(bar, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden'],
      });
      if ('ResizeObserver' in window) new ResizeObserver(this.sync).observe(bar);
      window.addEventListener('resize', this.sync);
      // Webfont kann die Segmentbreite nach dem ersten Layout noch verschieben
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.sync);
      this.sync();
    }

    // Der Observer hört auf denselben Container, auf dem wir Klassen setzen —
    // deshalb pro Frame nur einmal messen und Klassen nur bei echter Änderung
    // anfassen, sonst triggert die eigene Änderung die nächste Runde.
    sync() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.measure();
      });
    }

    // offset* statt getBoundingClientRect: die Werte sind Layout-Maße und damit
    // unabhängig von der Druck-Skalierung (:active) des Segments. Bezugspunkt
    // ist die Padding-Kante des offsetParent — der Container ist position:
    // relative, also genau der Ursprung der absolut gesetzten Pille.
    measure() {
      const active = this.bar.querySelector(`${this.sel}.is-active`);
      // Breite 0 = Container gerade unsichtbar (hidden/display:none) — dann
      // wären alle Maße 0 und die Pille würde beim Einblenden springen.
      if (!active || active.offsetWidth === 0) {
        if (this.bar.classList.contains('has-thumb')) this.bar.classList.remove('has-thumb');
        return;
      }
      const s = this.bar.style;
      s.setProperty('--thumb-x', `${active.offsetLeft}px`);
      s.setProperty('--thumb-y', `${active.offsetTop}px`);
      s.setProperty('--thumb-w', `${active.offsetWidth}px`);
      s.setProperty('--thumb-h', `${active.offsetHeight}px`);
      if (!this.bar.classList.contains('has-thumb')) this.bar.classList.add('has-thumb');
      if (this.ready) return;
      this.ready = true;
      // Übergänge erst ab dem zweiten Frame — sonst gleitet die Pille beim
      // Seitenaufbau sichtbar von links an ihren Platz.
      requestAnimationFrame(() => this.bar.classList.add('is-ready'));
    }
  }

  // --- Eigene Auswahl-Komponente über den nativen Selects ---
  // Button + Panel mit Suchfeld und strukturierten Zeilen (Titel / kW-PS rechts /
  // Baujahr+Motorcode als Unterzeile). Der native Select bleibt Wertträger:
  // Auswahl setzt select.value und feuert 'change' — die Kaskade merkt nichts.
  class EbcSelect {
    constructor(select) {
      if (select.dataset.ebcSelect) return;
      select.dataset.ebcSelect = '1';
      this.select = select;

      this.wrap = document.createElement('div');
      this.wrap.className = 'ebc-select';
      select.parentNode.insertBefore(this.wrap, select);
      this.wrap.appendChild(select);
      select.classList.add('ebc-select__native');
      select.tabIndex = -1;

      this.btn = document.createElement('button');
      this.btn.type = 'button';
      this.btn.className = 'ebc-select__button';
      this.btn.setAttribute('aria-haspopup', 'listbox');
      this.btn.innerHTML = '<span class="ebc-select__label"></span><span class="ebc-select__chevron" aria-hidden="true"></span>';

      this.panel = document.createElement('div');
      this.panel.className = 'ebc-select__panel';
      this.panel.hidden = true;
      this.panel.innerHTML = `
        <input type="search" class="ebc-select__search" placeholder="Tippen zum Filtern …" aria-label="Liste filtern">
        <ul class="ebc-select__list" role="listbox"></ul>`;

      this.wrap.append(this.btn, this.panel);
      this.search = this.panel.querySelector('.ebc-select__search');
      this.list = this.panel.querySelector('.ebc-select__list');
      this.activeIdx = -1;

      select._ebcButton = this.btn;
      select._ebcOpen = () => this.open();

      this.btn.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open();
        }
      });
      this.search.addEventListener('input', () => this.renderList());
      this.search.addEventListener('keydown', (e) => {
        const rows = [...this.list.querySelectorAll('[data-value]')];
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (rows.length === 0) return;
          this.activeIdx = e.key === 'ArrowDown'
            ? Math.min(this.activeIdx + 1, rows.length - 1)
            : Math.max(this.activeIdx - 1, 0);
          rows.forEach((r, i) => r.classList.toggle('is-active', i === this.activeIdx));
          rows[this.activeIdx].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const target = this.activeIdx >= 0 ? rows[this.activeIdx] : rows[0];
          if (target) this.pick(target.dataset.value);
        } else if (e.key === 'Escape') {
          this.close();
          this.btn.focus();
        }
      });
      this.list.addEventListener('click', (e) => {
        const row = e.target.closest('[data-value]');
        if (row) this.pick(row.dataset.value);
      });
      document.addEventListener('click', (e) => {
        if (!this.panel.hidden && !this.wrap.contains(e.target)) this.close();
      });

      select.addEventListener('ebc:options', () => {
        this.syncLabel();
        this.syncDisabled();
        if (!this.panel.hidden) this.renderList();
      });
      select.addEventListener('change', () => this.syncLabel());
      new MutationObserver(() => this.syncDisabled()).observe(select, {
        attributes: true,
        attributeFilter: ['disabled'],
      });

      this.syncLabel();
      this.syncDisabled();
    }

    options() {
      if (this.select._ebcOptions) return this.select._ebcOptions;
      return [...this.select.options].slice(1).map((o) => ({ value: o.value, title: o.textContent.trim() }));
    }

    placeholder() {
      return this.select.options[0] ? this.select.options[0].textContent.trim() : '';
    }

    syncLabel() {
      const current = this.options().find((o) => String(o.value) === this.select.value);
      const label = this.btn.querySelector('.ebc-select__label');
      if (current) {
        label.innerHTML = `${esc(current.title)}${current.right ? `<span class="ebc-select__label-right">${esc(current.right)}</span>` : ''}`;
        this.btn.classList.add('has-value');
        this.btn.title = [current.title, current.right, current.sub].filter(Boolean).join(' · ');
      } else {
        label.textContent = this.placeholder();
        this.btn.classList.remove('has-value');
        this.btn.title = '';
      }
    }

    syncDisabled() {
      this.btn.disabled = this.select.disabled;
      if (this.select.disabled && !this.panel.hidden) this.close();
    }

    renderList() {
      const q = this.search.value.trim().toLowerCase();
      const opts = this.options().filter(
        (o) => q === '' || `${o.title} ${o.sub ?? ''} ${o.right ?? ''}`.toLowerCase().includes(q),
      );
      this.activeIdx = -1;
      let html = '';
      let currentGroup = null;
      for (const o of opts) {
        const g = o.group ?? null;
        if (g !== currentGroup) {
          if (g) html += `<li class="ebc-select__group">${esc(g)}</li>`;
          currentGroup = g;
        }
        const selected = String(o.value) === this.select.value;
        html += `<li>
          <button type="button" role="option" aria-selected="${selected}" data-value="${esc(String(o.value))}" class="ebc-select__option${selected ? ' is-selected' : ''}">
            <span class="ebc-select__option-main">
              <span class="ebc-select__option-title">${esc(o.title)}</span>
              ${o.sub ? `<span class="ebc-select__option-sub">${esc(o.sub)}</span>` : ''}
            </span>
            ${o.right ? `<span class="ebc-select__option-right">${esc(o.right)}</span>` : ''}
          </button>
        </li>`;
      }
      this.list.innerHTML = html || '<li class="ebc-select__empty">Keine Treffer</li>';
    }

    open() {
      if (this.select.disabled) return;
      this.search.value = '';
      this.renderList();
      this.panel.hidden = false;
      this.wrap.classList.add('is-open');
      this.btn.setAttribute('aria-expanded', 'true');
      // Suchfeld nur einblenden, wenn die Liste lang genug ist
      this.search.hidden = this.options().length <= 8;
      // Auf Touch-Geräten nicht fokussieren: die Tastatur verdeckte sonst die
      // halbe Liste, und iOS scrollte beim Wischen die Seite statt der Liste.
      // Stattdessen das Panel komplett ins Bild holen.
      if (window.matchMedia('(pointer: coarse)').matches) {
        requestAnimationFrame(() => this.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
      } else if (!this.search.hidden) {
        this.search.focus();
      }
    }

    close() {
      this.panel.hidden = true;
      this.wrap.classList.remove('is-open');
      this.btn.setAttribute('aria-expanded', 'false');
    }

    pick(value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
      this.syncLabel();
    }
  }

  // --- HSN/TSN-Suche (wiederverwendbar: Haupt-Finder + Collection-Finder) ---
  class HsnSearch {
    constructor(panel, makeLabels, onVehicle) {
      this.panel = panel;
      this.makeLabels = makeLabels;
      this.onVehicle = onVehicle;
      const btn = panel.querySelector('[data-finder-hsn-submit]');
      if (btn) btn.addEventListener('click', () => this.run());
      panel.querySelectorAll('[data-finder-hsn], [data-finder-tsn]').forEach((inp) => {
        inp.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') this.run();
        });
      });
    }

    async run() {
      const hsn = this.panel.querySelector('[data-finder-hsn]').value.trim();
      const tsn = this.panel.querySelector('[data-finder-tsn]').value.trim().toUpperCase();
      const out = this.panel.querySelector('[data-finder-hsn-result]');
      if (!/^\d{4}$/.test(hsn) || tsn.length < 3) {
        out.innerHTML = '<p class="finder__error">Bitte HSN (4 Ziffern, Feld 2.1) und TSN (erste 3 Zeichen, Feld 2.2) eingeben.</p>';
        return;
      }
      out.innerHTML = '<p class="finder__loading">Suche …</p>';
      const hits = await hsnLookup(`${hsn} ${tsn.slice(0, 3)}`);
      if (hits.length === 0) {
        out.innerHTML = `<p class="finder__error">Keine Zuordnung für ${esc(hsn)} ${esc(tsn)} gefunden — bitte über Marke/Modell suchen.</p>`;
        return;
      }
      const detailed = [];
      for (const [tecId, makeSlug] of hits) {
        const chunks = await getVehChunks(makeSlug);
        for (const c of chunks) {
          const t = c.types.find((x) => x[0] === tecId);
          if (t) {
            detailed.push({ tecId, makeSlug, model: c.models[t[1]], t });
            break;
          }
        }
      }
      const pick = (d) => {
        const label = vehicleLabel(this.makeLabels.get(d.makeSlug) || d.makeSlug, d.model, d.t);
        this.onVehicle({ tecId: d.tecId, make: d.makeSlug, label, hsnTsn: `${hsn} ${tsn.slice(0, 3)}` });
        out.innerHTML = '';
      };
      if (detailed.length === 1) {
        pick(detailed[0]);
        return;
      }
      out.innerHTML =
        '<p>Mehrere Fahrzeuge gefunden — bitte auswählen:</p><ul class="finder__hsn-hits" role="list">' +
        detailed
          .map(
            (d, i) =>
              `<li><button type="button" class="finder__hsn-hit" data-hit="${i}">${esc(this.makeLabels.get(d.makeSlug) || d.makeSlug)} ${esc(d.model)} — ${esc(typeLabel(d.t))}</button></li>`,
          )
          .join('') +
        '</ul>';
      out.querySelectorAll('[data-hit]').forEach((b) => {
        b.addEventListener('click', () => pick(detailed[parseInt(b.dataset.hit, 10)]));
      });
    }
  }

  // --- Artikelnummern-Suche (wiederverwendbar) ---
  class SkuSearch {
    constructor(panel) {
      this.panel = panel;
      const btn = panel.querySelector('[data-finder-sku-submit]');
      const input = panel.querySelector('[data-finder-sku]');
      if (btn) btn.addEventListener('click', () => this.run());
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') this.run();
        });
      }
    }

    async run() {
      // „EBC-DP4680R" und „DP4680R" finden denselben Artikel
      const input = this.panel.querySelector('[data-finder-sku]').value.trim();
      const raw = baseSku(input);
      const out = this.panel.querySelector('[data-finder-sku-result]');
      if (raw.length < 2) return;
      out.innerHTML = '<p class="finder__loading">Suche …</p>';
      // Beide Bereiche durchsuchen (PKW + Motorrad)
      const [prices, motoPrices] = await Promise.all([
        getPrices(),
        HAS_MOTO ? getMotoPrices().catch(() => null) : Promise.resolve(null),
      ]);
      if (prices[raw] || (motoPrices && motoPrices.skus[raw])) {
        window.location.href = productUrl(raw);
        return;
      }
      const matches = Object.keys(prices)
        .filter((sku) => sku.startsWith(raw))
        .map((sku) => ({ sku, area: 'Pkw' }));
      if (motoPrices) {
        for (const sku of Object.keys(motoPrices.skus)) {
          if (sku.startsWith(raw)) matches.push({ sku, area: 'Motorrad' });
        }
      }
      const top = matches.slice(0, 12);
      out.innerHTML =
        top.length === 0
          ? `<p class="finder__error">Keine Artikelnummer „${esc(input)}" gefunden.</p>`
          : '<ul class="finder__sku-hits" role="list">' +
            top
              .map(
                (m) =>
                  `<li><a href="${productUrl(m.sku)}">${esc(shopSku(m.sku))}${HAS_MOTO ? ` <span class="finder__sku-area">${esc(m.area)}</span>` : ''}</a></li>`,
              )
              .join('') +
            '</ul>';
    }
  }

  // --- Finder-Widget ---
  // Check-Modus (PDP-Fahrzeug-Check, data-finder-mode="check"): nach der
  // Fahrzeugwahl nur Fitments laden + Fahrzeug merken, KEINE Trefferliste
  // rendern — Badge, Fahrzeugliste und Empfehlungen reagieren selbst auf
  // finder:changed. Spart Preise/Bilder/Hunderte Listeneinträge pro Prüfung.
  async function selectVehicleOnly(out, vehicle) {
    if (out) out.innerHTML = '<p class="finder__loading">Passung wird geprüft …</p>';
    try {
      const { tuples } = await getFitments(vehicle.tecId);
      setVehicle(vehicle, tuples.map((t) => t[0]));
      if (out) out.innerHTML = '';
    } catch {
      if (out) out.innerHTML = '<p class="finder__error">Die Daten konnten nicht geladen werden — bitte versuchen Sie es gleich noch einmal.</p>';
    }
  }
  async function selectMotoVehicleOnly(out, vehicle) {
    if (out) out.innerHTML = '<p class="finder__loading">Passung wird geprüft …</p>';
    try {
      const { tuples } = await getMotoFitments(vehicle.vehId);
      setMotoVehicle(vehicle, tuples.map((t) => t[0]));
      if (out) out.innerHTML = '';
    } catch {
      if (out) out.innerHTML = '<p class="finder__error">Die Daten konnten nicht geladen werden — bitte versuchen Sie es gleich noch einmal.</p>';
    }
  }

  class Finder {
    constructor(root) {
      this.root = root;
      this.results = root.querySelector('[data-finder-results]');
      this.makeLabels = CONFIG ? new Map(CONFIG.makes.map(([slug, label]) => [slug, label])) : new Map();
      this.caliperUnavailable = false;
      this.checkMode = root.dataset.finderMode === 'check';
      const showResults = this.checkMode
        ? (v) => selectVehicleOnly(this.results, v)
        : (v) => renderResults(this.results, v);
      const showMoto = this.checkMode
        ? (v) => selectMotoVehicleOnly(this.results, v)
        : (v) => renderMotoResults(this.results, v);
      this.initTabs();
      if (CONFIG) {
        const cars = CONFIG.makes.filter(([, label]) => !isCaliperMake(label));
        const calipers = CONFIG.makes.filter(([, label]) => isCaliperMake(label));
        const vehiclePanel = root.querySelector('[data-finder-panel="vehicle"]');
        const caliperPanel = root.querySelector('[data-finder-panel="caliper"]');
        if (vehiclePanel) new Cascade(vehiclePanel, cars, this.makeLabels, showResults);
        if (caliperPanel && calipers.length > 0) {
          new Cascade(caliperPanel, calipers, this.makeLabels, showResults);
        } else if (caliperPanel) {
          caliperPanel.hidden = true;
          this.caliperUnavailable = true;
          const tab = root.querySelector('[data-finder-tab="caliper"]');
          if (tab) tab.hidden = true;
        }
        const motoPanel = root.querySelector('[data-finder-panel="moto-vehicle"]');
        if (motoPanel && HAS_MOTO) {
          new MotoCascade(motoPanel, showMoto);
        }
      }
      const hsnPanel = root.querySelector('[data-finder-panel="hsn"]');
      if (hsnPanel) {
        new HsnSearch(hsnPanel, this.makeLabels, showResults);
      }
      const skuPanel = root.querySelector('[data-finder-panel="sku"]');
      if (skuPanel) new SkuSearch(skuPanel);
      this.initAreas();
      this.restoreSaved();
    }

    // Gemerktes Fahrzeug beim Laden wiederherstellen — nur der Seiten-Finder
    // (data-finder-restore in sections/vehicle-finder.liquid), nicht Hero oder
    // Check-Modus. Trefferliste sofort rendern; mit #ergebnisse in der URL
    // (Direktlinks aus Kategorie-Leerzustand und „passt nicht“-Badge) hinscrollen.
    restoreSaved() {
      if (this.checkMode || !this.results || !this.root.hasAttribute('data-finder-restore')) return;
      const moto = this.area === 'moto';
      const s = moto ? getMotoState() : getState();
      if (!s) return;
      const done = moto
        ? renderMotoResults(this.results, s.vehicle, { scroll: false })
        : renderResults(this.results, s.vehicle, { scroll: false });
      if (location.hash === '#ergebnisse') {
        done.then(() => this.results.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    }

    initTabs() {
      const tabs = this.root.querySelectorAll('[data-finder-tab]');
      tabs.forEach((btn) => {
        btn.addEventListener('click', () => this.activateTab(btn.dataset.finderTab));
      });
      // Bewusst KEINE gleitende Pille auf dieser Reihe — die füllt sich an Ort
      // und Stelle (finder.css). Der wandernde Indikator bleibt Ebene 1.
    }

    activateTab(name) {
      this.root.querySelectorAll('[data-finder-tab]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.finderTab === name);
      });
      this.root.querySelectorAll('[data-finder-panel]').forEach((p) => {
        p.hidden = p.dataset.finderPanel !== name;
      });
    }

    // Bereichs-Umschalter Pkw | Motorrad (nur wenn Moto-Daten released sind)
    initAreas() {
      const bar = this.root.querySelector('[data-finder-areas]');
      if (!bar) return;
      if (!HAS_MOTO) {
        bar.hidden = true;
        const motoTab = this.root.querySelector('[data-finder-tab="moto-vehicle"]');
        if (motoTab) motoTab.hidden = true;
        return;
      }
      const TABS_BY_AREA = {
        pkw: ['vehicle', 'hsn', 'sku', 'caliper'],
        moto: ['moto-vehicle', 'sku'],
      };
      const btns = [...bar.querySelectorAll('[data-finder-area]')];
      const apply = (area, keepResults) => {
        this.area = area;
        btns.forEach((b) => b.classList.toggle('is-active', b.dataset.finderArea === area));
        this.root.querySelectorAll('[data-finder-tab]').forEach((t) => {
          const allowed = TABS_BY_AREA[area].includes(t.dataset.finderTab);
          t.hidden = !allowed || (t.dataset.finderTab === 'caliper' && this.caliperUnavailable);
        });
        this.activateTab(area === 'moto' ? 'moto-vehicle' : 'vehicle');
        if (!keepResults && this.results) this.results.innerHTML = '';
        if (this.checkMode) return; // Produktseite überschreibt die globale Bereichswahl nicht
        try {
          localStorage.setItem(LS_KEY_AREA, area);
        } catch {
          /* Storage optional */
        }
      };
      btns.forEach((b) => b.addEventListener('click', () => apply(b.dataset.finderArea, false)));
      let initial = 'pkw';
      if (!this.checkMode) {
        try {
          if (localStorage.getItem(LS_KEY_AREA) === 'moto') initial = 'moto';
        } catch {
          /* Storage optional */
        }
      }
      if (this.root.dataset.area === 'moto') initial = 'moto';
      // Direktlinks (Kategorie-Leerzustand, „passt nicht“-Badge) nennen den Bereich explizit
      if (!this.checkMode) {
        const q = new URLSearchParams(location.search).get('bereich');
        if (q === 'pkw' || q === 'moto') initial = q;
      }
      apply(initial, true);
      if (this.checkMode) {
        // Check-Modus: der Bereich ist durch das Produkt fix — Umschalter aus
        // (SegmentedThumb misst Layout, im versteckten Panel sinnlos)
        bar.hidden = true;
        return;
      }
      new SegmentedThumb(bar, '[data-finder-area]');
    }

    initCascade() {
      if (!CONFIG) return;
      // Automarken und Bremssattel-Hersteller (Rennsport) getrennt gruppieren
      const cars = CONFIG.makes.filter(([, label]) => !isCaliperMake(label));
      const calipers = CONFIG.makes.filter(([, label]) => isCaliperMake(label));
      const opts = (list) =>
        list.map(([slug, label]) => `<option value="${esc(slug)}">${esc(label)}</option>`).join('');
      this.selMake.innerHTML =
        '<option value="">Marke wählen …</option>' +
        `<optgroup label="Fahrzeuge">${opts(cars)}</optgroup>` +
        (calipers.length > 0 ? `<optgroup label="Bremssättel (Rennsport)">${opts(calipers)}</optgroup>` : '');
      this.makeLabels = new Map(CONFIG.makes.map(([slug, label]) => [slug, label]));

      this.selMake.addEventListener('change', () => this.onMake());
      this.selModel.addEventListener('change', () => this.onModel());
      this.selType.addEventListener('change', () => this.onType());
    }

    async onMake() {
      const slug = this.selMake.value;
      this.selModel.innerHTML = '<option value="">Modell wählen …</option>';
      this.selType.innerHTML = '<option value="">Typ wählen …</option>';
      this.selModel.disabled = this.selType.disabled = true;
      if (!slug) return;
      this.chunks = await getVehChunks(slug);
      const models = [];
      for (const c of this.chunks) for (const m of c.models) if (!models.includes(m)) models.push(m);
      this.selModel.innerHTML =
        '<option value="">Modell wählen …</option>' +
        models.map((m) => `<option value="${esc(m)}">${esc(m)}</option>`).join('');
      this.selModel.disabled = false;
    }

    onModel() {
      const model = this.selModel.value;
      this.selType.innerHTML = '<option value="">Typ wählen …</option>';
      this.selType.disabled = true;
      if (!model) return;
      this.currentTypes = [];
      for (const c of this.chunks) {
        for (const t of c.types) {
          if (c.models[t[1]] === model) this.currentTypes.push(t);
        }
      }
      this.selType.innerHTML =
        '<option value="">Typ wählen …</option>' +
        this.currentTypes.map((t) => `<option value="${t[0]}">${esc(typeLabel(t))}</option>`).join('');
      this.selType.disabled = false;
    }

    onType() {
      const tecId = parseInt(this.selType.value, 10);
      if (!tecId) return;
      const t = this.currentTypes.find((x) => x[0] === tecId);
      const vehicle = {
        tecId,
        make: this.selMake.value,
        label: vehicleLabel(this.makeLabels.get(this.selMake.value), this.selModel.value, t),
        hsnTsn: t[12] && t[12].length > 0 ? t[12][0] : null,
      };
      renderResults(this.results, vehicle);
    }

  }

  // --- Collection-Finder: Collection ∩ Fahrzeug (auf Serien-/Kategorie-Seiten) ---
  const TYP_BY_COLLECTION = {
    bremsbelaege: 'belag',
    bremsscheiben: 'scheibe',
    bremstrommeln: 'trommel',
    bremsbacken: 'backe',
    handbremsbacken: 'handbremsbacke',
    verschleisswarnkontakte: 'vwk',
  };

  // Moto-Kategorie-Collections → Ergebnisfilter; Serien-Collections matchen über den Serien-Slug
  const MOTO_KAT_BY_COLLECTION = {
    'motorrad-bremsbelaege': 'belaege',
    'motorrad-bremsscheiben': 'scheiben',
    'motorrad-kupplungen': 'kupplung',
    'motorrad-stahlflex': 'stahlflex',
    'motorrad-zubehoer': 'zubehoer',
  };

  // Direktlink in die Trefferliste der Finder-Seite: Bereich explizit, #ergebnisse
  // lässt die Seite das gemerkte Fahrzeug wiederherstellen und hinscrollen (Finder.restoreSaved)
  function finderResultsUrl(isMoto) {
    return `${FINDER_PAGE}?bereich=${isMoto ? 'moto' : 'pkw'}#ergebnisse`;
  }

  // Leerzustand der Kategorie-Trefferliste (Fahrzeug gemerkt, aber nichts passt):
  // Karte mit Fahrzeug-Icon, Titel, Text und den beiden Aktionen — statt einer
  // nackten Textzeile im grauen Band. Styles: ebc-base.css (.cf__empty*).
  function cfEmptyHtml(label, isMoto) {
    const fz = isMoto ? 'Motorrad' : 'Fahrzeug';
    return `<div class="cf__empty">
      <span class="cf__empty-icon" aria-hidden="true">${isMoto ? bikeIcon() : carIcon()}</span>
      <div class="cf__empty-body">
        <h3 class="cf__empty-title">Keine passenden Artikel in dieser Kategorie</h3>
        <p class="cf__empty-text">Für <strong>${esc(label)}</strong> führt EBC hier nichts — im Finder sehen Sie alle Teile, die zu Ihrem ${fz} passen.</p>
        <div class="cf__empty-actions">
          <a class="ebc-cta ebc-cta--dark ebc-cta--compact" href="${finderResultsUrl(isMoto)}"><span class="ebc-cta__label">Alle passenden Teile im Finder</span><span class="ebc-cta__dot" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12h13m0 0-5.5-5.5M18 12l-5.5 5.5"/></svg></span></a>
          <button type="button" class="ebc-cta ebc-cta--ghost ebc-cta--compact" data-cf-clear><span class="ebc-cta__label">${fz} entfernen</span><span class="ebc-cta__dot" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg></span></button>
        </div>
      </div>
    </div>`;
  }
  // „Fahrzeug entfernen“ in Kopf und Leerzustand verdrahten
  function bindCfClear(out, isMoto) {
    const clear = out.querySelector('[data-cf-clear]');
    if (clear) clear.addEventListener('click', isMoto ? clearMotoVehicle : clearVehicle);
  }

  class CollectionFinder {
    constructor(root) {
      this.root = root;
      this.handle = root.dataset.handle || '';
      this.isMoto = root.dataset.cfArea === 'moto';
      // Der Ergebnis-Container wird per Inline-Script der Section VOR die
      // Produktliste verschoben (nach der Serien-Info) — liegt dann also
      // außerhalb von root.
      this.out = root.querySelector('[data-cf-results]') || document.querySelector('[data-cf-results]');
      this.pick = root.querySelector('[data-cf-pick]');

      // Kompakte Tabs (Fahrzeug / Schlüsselnummer / Artikelnummer)
      const tabs = root.querySelectorAll('[data-cf-tab]');
      tabs.forEach((btn) => {
        btn.addEventListener('click', () => {
          tabs.forEach((b) => b.classList.toggle('is-active', b === btn));
          root.querySelectorAll('[data-cf-panel]').forEach((p) => {
            p.hidden = p.dataset.cfPanel !== btn.dataset.cfTab;
          });
        });
      });

      if (CONFIG && this.isMoto) {
        if (HAS_MOTO) {
          const cascadePanel = root.querySelector('[data-cf-moto-cascade]');
          if (cascadePanel) new MotoCascade(cascadePanel, (v) => this.selectMotoVehicle(v));
        }
        const skuPanel = root.querySelector('[data-cf-panel="sku"]');
        if (skuPanel) new SkuSearch(skuPanel);
      } else if (CONFIG) {
        const labels = new Map(CONFIG.makes.map(([slug, label]) => [slug, label]));
        const cars = CONFIG.makes.filter(([, label]) => !isCaliperMake(label));
        const calipers = CONFIG.makes.filter(([, label]) => isCaliperMake(label));
        const onVehicle = (v) => this.selectVehicle(v);

        const cascadePanel = root.querySelector('[data-cf-cascade]');
        if (cascadePanel) {
          new Cascade(
            cascadePanel,
            {
              groups: [
                { label: 'Fahrzeuge', items: cars },
                { label: 'Bremssättel (Rennsport)', items: calipers },
              ],
            },
            labels,
            onVehicle,
          );
        }
        const hsnPanel = root.querySelector('[data-cf-panel="hsn"]');
        if (hsnPanel) new HsnSearch(hsnPanel, labels, onVehicle);
        const skuPanel = root.querySelector('[data-cf-panel="sku"]');
        if (skuPanel) new SkuSearch(skuPanel);
      }
      document.addEventListener('finder:changed', () => this.render());
      this.render();
    }

    matches(line) {
      if (this.handle.indexOf('ebc-') === 0) return slugify(line.label) === this.handle.slice(4);
      const typ = TYP_BY_COLLECTION[this.handle];
      return typ ? line.typ === typ : false;
    }

    matchesMoto(item, prices) {
      const kat = MOTO_KAT_BY_COLLECTION[this.handle];
      if (kat) return motoGruppeKategorie(item.gruppeKey) === kat;
      if (this.handle.indexOf('motorrad-') === 0) {
        const serie = this.handle.slice(9);
        const p = prices.skus[item.sku];
        return p ? prices.linien[p[1]][1] === serie : false;
      }
      return false;
    }

    async selectVehicle(vehicle) {
      try {
        const { tuples } = await getFitments(vehicle.tecId);
        setVehicle(vehicle, tuples.map((t) => t[0])); // löst finder:changed aus → render()
      } catch {
        this.out.innerHTML = '<p class="finder__error">Daten konnten nicht geladen werden.</p>';
      }
    }

    async selectMotoVehicle(vehicle) {
      try {
        const { tuples } = await getMotoFitments(vehicle.vehId);
        setMotoVehicle(vehicle, tuples.map((t) => t[0]));
      } catch {
        this.out.innerHTML = '<p class="finder__error">Daten konnten nicht geladen werden.</p>';
      }
    }

    async render() {
      if (this.isMoto) {
        this.renderMoto();
        return;
      }
      const s = getState();
      if (!s) {
        if (this.pick) this.pick.hidden = false;
        this.out.innerHTML = '';
        return;
      }
      if (this.pick) this.pick.hidden = false; // Leiste bleibt auch mit Fahrzeug sichtbar
      this.out.innerHTML = `<div class="finder__skeleton"><div class="finder__skeleton-row"></div></div>`;
      try {
        const [{ tuples, zusatz, lines }, prices] = await Promise.all([
          getFitments(s.vehicle.tecId),
          getPrices(),
        ]);
        const items = [];
        for (const [sku, achse, zIdx, lineIdx] of tuples) {
          const line = lines[lineIdx];
          if (this.matches(line)) items.push({ sku, achse, zusatz: zusatz[zIdx], line });
        }
        if (items.length === 0) {
          this.out.innerHTML = cfEmptyHtml(s.vehicle.label, this.isMoto);
          bindCfClear(this.out, this.isMoto);
          return;
        }
        const byAchse = new Map();
        for (const item of items) {
          if (!byAchse.has(item.achse)) byAchse.set(item.achse, []);
          byAchse.get(item.achse).push(item);
        }
        let html = `<div class="cf__head">
          <span><strong>${items.length} Artikel</strong> aus dieser Kategorie passen für ${esc(s.vehicle.label)}</span>
          <button type="button" class="cf__clear" data-cf-clear>Fahrzeug entfernen</button>
        </div>`;
        for (const [achse, list] of [...byAchse.entries()].sort()) {
          // Gleiches Gerüst wie renderResults() auf der Finder-Seite (section.finder__group
          // liefert den Abstand zwischen den Achs-Gruppen — finder.css)
          html += `<section class="finder__group">
            <h3 class="finder__group-title">${achse ? axleIcon(achse) : ''}${esc(ACHSE_LABELS[achse] ?? achse)} <span class="finder__group-count">(${list.length})</span></h3>
            <ul class="finder__items" role="list">${list.map((item) => resultItemHtml(item, prices)).join('')}</ul>
          </section>`;
        }
        this.out.innerHTML = html;
        const clear = this.out.querySelector('[data-cf-clear]');
        if (clear) clear.addEventListener('click', clearVehicle);
        hydrateImages(this.out);
      } catch {
        this.out.innerHTML = '<p class="finder__error">Daten konnten nicht geladen werden — bitte Seite neu laden.</p>';
      }
    }

    async renderMoto() {
      const s = getMotoState();
      if (!s) {
        if (this.pick) this.pick.hidden = false;
        this.out.innerHTML = '';
        return;
      }
      if (this.pick) this.pick.hidden = false; // Leiste bleibt auch mit Fahrzeug sichtbar
      this.out.innerHTML = `<div class="finder__skeleton"><div class="finder__skeleton-row"></div></div>`;
      try {
        const [{ tuples, hinweise, gruppen }, prices] = await Promise.all([
          getMotoFitments(s.vehicle.vehId),
          getMotoPrices(),
        ]);
        const items = collapseMotoItems(
          tuples.map(([sku, gIdx, seite, pos, hIdx]) => {
            const g = gruppen[gIdx] || ['', ''];
            return { sku, gruppeKey: g[0], gruppeLabel: g[1], seite, pos, hinweis: hinweise[hIdx] || '' };
          }),
        ).filter((it) => this.matchesMoto(it, prices));
        if (items.length === 0) {
          this.out.innerHTML = cfEmptyHtml(s.vehicle.label, this.isMoto);
          bindCfClear(this.out, this.isMoto);
          return;
        }
        const bySeite = new Map();
        for (const item of items) {
          if (!bySeite.has(item.seite)) bySeite.set(item.seite, []);
          bySeite.get(item.seite).push(item);
        }
        let html = `<div class="cf__head">
          <span><strong>${items.length} Artikel</strong> aus dieser Kategorie passen für ${esc(s.vehicle.label)}</span>
          <button type="button" class="cf__clear" data-cf-clear>Fahrzeug entfernen</button>
        </div>`;
        const order = ['V', 'H', 'PB', ''];
        for (const seite of order) {
          const list = bySeite.get(seite);
          if (!list) continue;
          list.sort((a, b) => a.gruppeLabel.localeCompare(b.gruppeLabel) || a.sku.localeCompare(b.sku));
          html += `<section class="finder__group">
            <h3 class="finder__group-title">${motoAxleIcon(seite)}${esc(MOTO_SEITE_LABELS[seite] || 'Passend')} <span class="finder__group-count">(${list.length})</span></h3>
            <ul class="finder__items" role="list">${list.map((item) => motoResultItemHtml(item, prices)).join('')}</ul>
          </section>`;
        }
        this.out.innerHTML = html;
        const clear = this.out.querySelector('[data-cf-clear]');
        if (clear) clear.addEventListener('click', clearMotoVehicle);
        hydrateImages(this.out);
      } catch {
        this.out.innerHTML = '<p class="finder__error">Daten konnten nicht geladen werden — bitte Seite neu laden.</p>';
      }
    }
  }

  // --- PDP: fahrzeugbezogene Empfehlungen (Kombis + Alternativen) ---
  // Ab 750px zeigt ebc-related.liquid die Empfehlungen als Kartenraster (größere Bilder)
  const CARDS = { cards: window.matchMedia('(min-width: 750px)').matches };
  class RelatedItems {
    constructor(root) {
      this.root = root;
      this.sku = baseSku(root.dataset.sku);
      this.typ = root.dataset.typ || '';
      try {
        this.achsen = JSON.parse(root.dataset.achsen || '[]') || [];
      } catch {
        this.achsen = [];
      }
      document.addEventListener('finder:changed', () => this.render());
      this.render();
    }

    show(which) {
      ['combo', 'alt', 'universal'].forEach((k) => {
        const el = this.root.querySelector(`[data-rel-${k}]`);
        if (el) el.hidden = !which.includes(k);
      });
    }

    // Fahrzeug-Pillen in den Gruppenköpfen (ebc-related.liquid) füllen bzw. ausblenden
    setVehiclePill(label) {
      this.root.querySelectorAll('[data-rel-vehicle]').forEach((el) => {
        const l = el.querySelector('[data-rel-vehicle-label]');
        if (l) l.textContent = label || '';
        el.hidden = !label;
      });
    }

    matchesAchse(achse) {
      if (this.achsen.length === 0) return true;
      return this.achsen.includes(achse) || achse === 'VAHA' || this.achsen.includes('VAHA');
    }

    async render() {
      if (this.typ.indexOf('moto-') === 0) {
        this.renderMoto();
        return;
      }
      const s = getState();
      if (!s || !CONFIG) {
        this.renderUniversal();
        return;
      }
      try {
        const [{ tuples, zusatz, lines }, prices] = await Promise.all([
          getFitments(s.vehicle.tecId),
          getPrices(),
        ]);
        const items = tuples.map(([sku, achse, zIdx, lineIdx]) => ({
          sku,
          achse,
          zusatz: zusatz[zIdx],
          line: lines[lineIdx],
        }));

        // Kombis: andere Kategorie, gleiche Achse zuerst
        const combos = items
          .filter((i) => i.line.typ !== this.typ)
          .sort((a, b) => this.matchesAchse(b.achse) - this.matchesAchse(a.achse))
          .slice(0, 6);
        // Alternativen: gleiche Kategorie + Achse, anderer Artikel
        const alts = items
          .filter(
            (i) =>
              i.line.typ === this.typ &&
              i.sku.toUpperCase() !== this.sku &&
              this.matchesAchse(i.achse),
          )
          .slice(0, 6);

        if (combos.length === 0 && alts.length === 0) {
          this.renderUniversal();
          return;
        }
        this.setVehiclePill(s.vehicle.label);
        const comboList = this.root.querySelector('[data-rel-combo-list]');
        const altList = this.root.querySelector('[data-rel-alt-list]');
        if (comboList) comboList.innerHTML = combos.map((i) => resultItemHtml(i, prices, CARDS)).join('');
        if (altList) altList.innerHTML = alts.map((i) => resultItemHtml(i, prices, CARDS)).join('');
        const visible = [];
        if (combos.length > 0) visible.push('combo');
        if (alts.length > 0) visible.push('alt');
        this.show(visible);
        hydrateImages(this.root);
      } catch {
        this.renderUniversal();
      }
    }

    // Moto-PDP: Empfehlungen aus dem gemerkten Motorrad (Kombis + Alternativen)
    async renderMoto() {
      const s = getMotoState();
      if (!s || !HAS_MOTO) {
        this.renderUniversal();
        return;
      }
      try {
        const [{ tuples, hinweise, gruppen }, prices] = await Promise.all([
          getMotoFitments(s.vehicle.vehId),
          getMotoPrices(),
        ]);
        const kat = this.typ.slice(5);
        const items = collapseMotoItems(
          tuples.map(([sku, gIdx, seite, pos, hIdx]) => {
            const g = gruppen[gIdx] || ['', ''];
            return { sku, gruppeKey: g[0], gruppeLabel: g[1], seite, pos, hinweis: hinweise[hIdx] || '' };
          }),
        );
        const combos = items.filter((i) => motoGruppeKategorie(i.gruppeKey) !== kat).slice(0, 6);
        const alts = items
          .filter((i) => motoGruppeKategorie(i.gruppeKey) === kat && i.sku.toUpperCase() !== this.sku)
          .slice(0, 6);
        if (combos.length === 0 && alts.length === 0) {
          this.renderUniversal();
          return;
        }
        this.setVehiclePill(s.vehicle.label);
        const comboList = this.root.querySelector('[data-rel-combo-list]');
        const altList = this.root.querySelector('[data-rel-alt-list]');
        if (comboList) comboList.innerHTML = combos.map((i) => motoResultItemHtml(i, prices, CARDS)).join('');
        if (altList) altList.innerHTML = alts.map((i) => motoResultItemHtml(i, prices, CARDS)).join('');
        const visible = [];
        if (combos.length > 0) visible.push('combo');
        if (alts.length > 0) visible.push('alt');
        this.show(visible);
        hydrateImages(this.root);
      } catch {
        this.renderUniversal();
      }
    }

    async renderUniversal() {
      this.setVehiclePill('');
      this.show(['universal']);
      const list = this.root.querySelector('[data-rel-universal-list]');
      if (!list || list.children.length > 0) return;
      try {
        const res = await fetch('/collections/finder-universal/products.json?limit=4');
        if (!res.ok) return;
        const { products } = await res.json();
        list.innerHTML = (products || [])
          .map((p) => {
            const v = p.variants && p.variants[0];
            const cent = v ? Math.round(parseFloat(v.price) * 100) : null;
            const uvp = v && v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null;
            const img = p.images && p.images[0] ? p.images[0].src : null;
            const sep = img && img.includes('?') ? '&' : '?';
            return `<li class="finder__item">
              <a class="finder__item-link" href="/products/${esc(p.handle)}">
                <span class="finder__item-img" aria-hidden="true">${img ? `<img src="${esc(img)}${sep}width=${CARDS.cards ? 480 : 120}" alt="" loading="lazy" width="${CARDS.cards ? 240 : 60}" height="${CARDS.cards ? 240 : 60}">` : ''}</span>
                <span class="finder__item-main">
                  <span class="finder__item-line">${esc(p.title)}</span>
                  ${v && v.sku ? `<span class="finder__item-sku">Art.-Nr. ${esc(shopSku(baseSku(v.sku)))}</span>${stockSlot(baseSku(v.sku))}` : ''}
                </span>
                ${priceBlock(cent, uvp)}
              </a>
            </li>`;
          })
          .join('');
        hydrateStock(list);
      } catch {
        /* optional */
      }
    }
  }

  // --- PDP: Lightbox-Galerie (Vollbild-Overlay, Thumbnails, Klick-Zoom) ---
  class PdpGallery {
    constructor() {
      this.items = [...document.querySelectorAll('.product__media-item img')];
      if (this.items.length === 0) return;
      this.srcs = this.items.map((img) => this.sized(img.currentSrc || img.src, 1600));
      this.thumbs = this.items.map((img) => this.sized(img.currentSrc || img.src, 160));
      this.title = (document.querySelector('.ebc-pdp__title, .product__title')?.textContent || '').trim();
      this.el = null;
      this.idx = 0;
      this.items.forEach((img, i) => {
        const item = img.closest('.product__media-item');
        if (!item) return;
        item.classList.add('ebc-zoomable');
        item.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(i);
        });
      });
      document.addEventListener('keydown', (e) => {
        if (!this.el || this.el.hidden) return;
        if (e.key === 'ArrowRight') this.nav(1);
        if (e.key === 'ArrowLeft') this.nav(-1);
        if (e.key === 'Escape') this.close();
      });
    }

    sized(src, width) {
      try {
        const u = new URL(src, location.href);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch {
        return src;
      }
    }

    ensure() {
      if (this.el) return;
      this.el = document.createElement('div');
      this.el.className = 'ebc-lightbox';
      this.el.hidden = true;
      this.el.innerHTML = `
        <div class="ebc-lightbox__bar">
          <span class="ebc-lightbox__title">${esc(this.title)}</span>
          <span class="ebc-lightbox__counter" data-lb-counter></span>
          <button type="button" class="ebc-lightbox__close" aria-label="Schließen" data-lb-close>×</button>
        </div>
        <div class="ebc-lightbox__stage" data-lb-stage>
          <button type="button" class="ebc-lightbox__nav" aria-label="Vorheriges Bild" data-lb-prev>‹</button>
          <figure class="ebc-lightbox__frame" data-lb-frame>
            <img data-lb-img src="" alt="${esc(this.title)}">
          </figure>
          <button type="button" class="ebc-lightbox__nav" aria-label="Nächstes Bild" data-lb-next>›</button>
        </div>
        <div class="ebc-lightbox__thumbs" data-lb-thumbs>
          ${this.thumbs
            .map(
              (t, i) =>
                `<button type="button" class="ebc-lightbox__thumb" data-lb-thumb="${i}" aria-label="Bild ${i + 1}"><img src="${esc(t)}" alt="" loading="lazy" width="56" height="56"></button>`,
            )
            .join('')}
        </div>`;
      document.body.appendChild(this.el);
      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-lb-close]')) return this.close();
        if (e.target.closest('[data-lb-prev]')) return this.nav(-1);
        if (e.target.closest('[data-lb-next]')) return this.nav(1);
        const thumb = e.target.closest('[data-lb-thumb]');
        if (thumb) {
          this.idx = parseInt(thumb.dataset.lbThumb, 10);
          this.update();
          return;
        }
        const img = e.target.closest('[data-lb-img]');
        if (img) return this.toggleZoom(e);
        // Klick auf freie Fläche (Stage/Bar außerhalb der Bedienelemente) schließt
        if (!e.target.closest('[data-lb-frame]')) this.close();
      });
    }

    toggleZoom(e) {
      const frame = this.el.querySelector('[data-lb-frame]');
      const img = this.el.querySelector('[data-lb-img]');
      if (frame.classList.contains('is-zoomed')) {
        frame.classList.remove('is-zoomed');
        img.style.transformOrigin = '';
        return;
      }
      const rect = img.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
      frame.classList.add('is-zoomed');
    }

    open(i) {
      this.ensure();
      this.idx = i;
      this.update();
      this.el.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.el) return;
      this.el.hidden = true;
      this.el.querySelector('[data-lb-frame]').classList.remove('is-zoomed');
      document.body.style.overflow = '';
    }

    nav(dir) {
      this.idx = (this.idx + dir + this.srcs.length) % this.srcs.length;
      this.update();
    }

    update() {
      this.el.querySelector('[data-lb-frame]').classList.remove('is-zoomed');
      this.el.querySelector('[data-lb-img]').src = this.srcs[this.idx];
      this.el.querySelector('[data-lb-counter]').textContent = `${this.idx + 1} / ${this.srcs.length}`;
      this.el.querySelectorAll('[data-lb-thumb]').forEach((b) => {
        b.classList.toggle('is-active', parseInt(b.dataset.lbThumb, 10) === this.idx);
      });
      const single = this.srcs.length < 2;
      this.el.querySelectorAll('[data-lb-prev], [data-lb-next]').forEach((b) => (b.hidden = single));
      this.el.querySelector('[data-lb-thumbs]').hidden = single;
    }
  }

  // --- "Meine Fahrzeuge" (Garage): EINE Pille im Header (Icon + Kurzname,
  // zweites Fahrzeug als kleiner Marker) mit Dropdown-Panel; im Mobile-Drawer
  // und Finder-Aside ([data-chip-inline]) als offene Liste ohne Trigger. ---
  const shortLabel = (l) => String(l || '').replace(/\s*\([^)]*\)\s*$/, '');
  const ARROW_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13m0 0-5.5-5.5M18 12l-5.5 5.5"/></svg>';
  function garageItem(kind, label) {
    const isMoto = kind === 'moto';
    return `<li class="ebc-garage__item">
      <span class="ebc-garage__icon" aria-hidden="true">${isMoto ? bikeIcon() : carIcon()}</span>
      <span class="ebc-garage__text">
        <span class="ebc-garage__label">${esc(label)}</span>
        <span class="ebc-garage__sub">${isMoto ? 'Motorrad' : 'Pkw'}</span>
      </span>
      <button type="button" class="ebc-garage__remove" aria-label="${isMoto ? 'Motorrad' : 'Fahrzeug'} entfernen" title="Entfernen" data-finder-clear="${kind}">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
      </button>
    </li>`;
  }
  function renderChips() {
    const s = getState();
    const ms = getMotoState();
    document.querySelectorAll('[data-finder-chip]').forEach((el) => {
      const inline = el.hasAttribute('data-chip-inline');
      if (!s && !ms) {
        el.dataset.open = '';
        el.innerHTML = `<a class="finder-chip finder-chip--empty ebc-garage__empty" href="${FINDER_PAGE}">${carIcon()}<span>Fahrzeug wählen</span></a>`;
        return;
      }
      const keepOpen = el.dataset.open === 'true';
      const items = (s ? garageItem('pkw', s.vehicle.label) : '') + (ms ? garageItem('moto', ms.vehicle.label) : '');
      const panel = `<div class="ebc-garage__panel"${inline || keepOpen ? '' : ' hidden'}>
        ${inline ? '' : '<div class="ebc-garage__head">Meine Fahrzeuge</div>'}
        <ul class="ebc-garage__list" role="list">${items}</ul>
        <a class="ebc-arrow-link ebc-garage__link" href="${FINDER_PAGE}">Anderes Fahrzeug wählen ${ARROW_SVG}</a>
      </div>`;
      const primary = s || ms;
      const trigger = inline
        ? ''
        : `<button type="button" class="ebc-garage__trigger" aria-haspopup="true" aria-expanded="${keepOpen}" title="Meine Fahrzeuge" data-garage-toggle>
        <span class="ebc-garage__trigger-icon" aria-hidden="true">${s ? carIcon() : bikeIcon()}</span>
        <span class="ebc-garage__name">${esc(shortLabel(primary.vehicle.label))}</span>
        ${s && ms ? `<span class="ebc-garage__more" aria-hidden="true" title="${esc(ms.vehicle.label)}">${bikeIcon()}</span>` : ''}
        <svg class="ebc-garage__chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
      </button>`;
      el.innerHTML = `<div class="ebc-garage${inline ? ' ebc-garage--inline' : ''}" data-garage>${trigger}${panel}</div>`;
      el.querySelectorAll('[data-finder-clear]').forEach((btn) => {
        btn.addEventListener('click', () => (btn.dataset.finderClear === 'moto' ? clearMotoVehicle() : clearVehicle()));
      });
    });
  }
  function setGarageOpen(mount, open) {
    const panel = mount.querySelector('.ebc-garage__panel');
    const btn = mount.querySelector('[data-garage-toggle]');
    if (!panel || !btn) return;
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    mount.dataset.open = open ? 'true' : '';
  }
  document.addEventListener('click', (e) => {
    // Der ×-Button löst ein Neu-Rendern aus und ist hier schon aus dem DOM —
    // das wäre sonst fälschlich ein "Außenklick", der das Panel schließt
    if (!e.target.isConnected) return;
    const toggle = e.target.closest('[data-garage-toggle]');
    document.querySelectorAll('[data-finder-chip]:not([data-chip-inline])').forEach((mount) => {
      if (toggle && mount.contains(toggle)) {
        setGarageOpen(mount, mount.dataset.open !== 'true');
      } else if (!mount.contains(e.target)) {
        setGarageOpen(mount, false); // Außenklick schließt; Klick im Panel (×, Link) lässt es offen
      }
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelectorAll('[data-finder-chip]:not([data-chip-inline])').forEach((m) => setGarageOpen(m, false));
  });

  // --- Badges ("Passt für Ihr Fahrzeug") — Bereich kommt aus data-bereich (pkw|moto) ---
  function renderBadges() {
    const s = getState();
    const ms = getMotoState();
    document.querySelectorAll('[data-finder-badge]').forEach((el) => {
      const sku = baseSku(el.dataset.sku);
      const isMoto = el.dataset.bereich === 'moto';
      const state = isMoto ? ms : s;
      if (!state || !sku) {
        el.hidden = true;
        el.innerHTML = '';
        return;
      }
      if (el.dataset.universal === 'true') {
        el.hidden = false;
        el.dataset.fit = 'fit';
        el.innerHTML = `<span class="finder-badge finder-badge--fit">✓ Passt für alle Fahrzeuge</span>`;
        return;
      }
      const fits = state.skus.map((x) => x.toUpperCase()).includes(sku);
      el.hidden = false;
      el.dataset.fit = fits ? 'fit' : 'nofit';
      // data-badge-plain (PDP-Fitcheck): ohne Finder-Link — die Aktionen sind eigene Buttons
      const plain = el.dataset.badgePlain === 'true';
      el.innerHTML = fits
        ? `<span class="finder-badge finder-badge--fit">✓ Passt für: ${esc(state.vehicle.label)}</span>`
        : plain
          ? `<span class="finder-badge finder-badge--nofit">✗ Passt nicht für: ${esc(state.vehicle.label)}</span>`
          : `<span class="finder-badge finder-badge--nofit">✗ Passt nicht für: ${esc(state.vehicle.label)} — <a href="${finderResultsUrl(isMoto)}">passende Teile finden</a></span>`;
    });
  }

  // --- PDP: Fahrzeug-Check-Karte (Frage-Karte ↔ Badge + "Fahrzeug ändern") ---
  // Der Finder steckt in einem Modal (.ebc-fitmodal). Es wird beim ersten Öffnen
  // an <body> gehängt (die sticky Infospalte würde position:fixed sonst einfangen)
  // und über box._fitModal wiedergefunden.
  function fitcheckModal(box) {
    if (box._fitModal) return box._fitModal;
    const m = box.querySelector('[data-fitcheck-modal]');
    if (!m) return null;
    document.body.appendChild(m);
    box._fitModal = m;
    m._fitBox = box;
    return m;
  }
  // Zwei Modi: 'check' = Suchmaske (Fahrzeug wählen), 'results' = komplette
  // Finder-Trefferliste für das gemerkte Fahrzeug ("Passende Teile ansehen").
  function setFitcheckMode(m, mode) {
    m.dataset.mode = mode;
    m.classList.toggle('ebc-fitmodal--results', mode === 'results');
    const layout = m.querySelector('.finder__layout');
    if (layout) layout.hidden = mode !== 'check';
    const title = m.querySelector('[data-fitmodal-title]');
    const text = m.querySelector('[data-fitmodal-text]');
    if (!m._defaults && title && text) m._defaults = { title: title.textContent, text: text.textContent };
    if (mode === 'check' && m._defaults) {
      title.textContent = m._defaults.title;
      text.textContent = m._defaults.text;
      const out = m.querySelector('[data-finder-results]');
      if (out) out.innerHTML = '';
    }
  }
  function setFitcheckPanel(box, open, mode = 'check') {
    const m = fitcheckModal(box);
    if (!m) return;
    if (open) setFitcheckMode(m, mode);
    m.hidden = !open;
    document.body.style.overflow = open ? 'hidden' : '';
    box.querySelectorAll('[data-fitcheck-toggle]').forEach((b) => b.setAttribute('aria-expanded', String(open && mode === 'check')));
    if (open && mode === 'check') {
      const first = m.querySelector('[data-finder-panel]:not([hidden]) button, [data-finder-panel]:not([hidden]) input');
      if (first) first.focus({ preventScroll: true });
    } else if (!open) {
      const btn = box.querySelector('[data-fitcheck-toggle]:not([hidden])');
      if (btn) btn.focus({ preventScroll: true });
    }
  }
  async function showFitcheckParts(box) {
    const m = fitcheckModal(box);
    if (!m) return;
    const isMoto = box.dataset.bereich === 'moto';
    const state = isMoto ? getMotoState() : getState();
    if (!state) return;
    setFitcheckPanel(box, true, 'results');
    const title = m.querySelector('[data-fitmodal-title]');
    const text = m.querySelector('[data-fitmodal-text]');
    if (title) title.textContent = `Passende Teile für Ihr ${isMoto ? 'Motorrad' : 'Fahrzeug'}`;
    if (text) text.textContent = state.vehicle.label;
    const out = m.querySelector('[data-finder-results]');
    if (!out) return;
    const panel = m.querySelector('.ebc-modal__panel');
    if (panel) panel.scrollTop = 0;
    // renderResults ruft intern setVehicle (finder:changed) — renderFitchecks
    // lässt das Modal im Ergebnis-Modus bewusst offen.
    if (isMoto) await renderMotoResults(out, state.vehicle);
    else await renderResults(out, state.vehicle);
    // renderResults scrollt die Liste per scrollIntoView an — der Modal-Kopf
    // soll aber sichtbar bleiben
    if (panel) requestAnimationFrame(() => (panel.scrollTop = 0));
  }
  function renderFitchecks() {
    const s = getState();
    const ms = getMotoState();
    document.querySelectorAll('[data-fitcheck]').forEach((box) => {
      const has = !!(box.dataset.bereich === 'moto' ? ms : s);
      const badge = box.querySelector('[data-finder-badge]');
      const fit = has && badge ? badge.dataset.fit || '' : '';
      const ask = box.querySelector('[data-fitcheck-ask]');
      const actions = box.querySelector('[data-fitcheck-actions]');
      const parts = box.querySelector('[data-fitcheck-parts]');
      if (ask) ask.hidden = has;
      if (actions) actions.hidden = !has;
      if (parts) parts.hidden = !(has && fit === 'nofit');
      box.dataset.fit = fit;
      const m = box._fitModal;
      if (!m || m.hidden) return;
      // Fahrzeug gerade gewählt → Suchmaske schließen; Fahrzeug entfernt → Ergebnisliste schließen
      if (has && m.dataset.mode !== 'results') setFitcheckPanel(box, false);
      if (!has && m.dataset.mode === 'results') setFitcheckPanel(box, false);
    });
  }
  document.addEventListener('click', (e) => {
    const parts = e.target.closest('[data-fitcheck-parts]');
    if (parts) {
      const box = parts.closest('[data-fitcheck]');
      if (box) showFitcheckParts(box);
      return;
    }
    const toggle = e.target.closest('[data-fitcheck-toggle]');
    if (toggle) {
      const box = toggle.closest('[data-fitcheck]');
      const m = box && fitcheckModal(box);
      if (m) setFitcheckPanel(box, m.hidden || m.dataset.mode === 'results', 'check');
      return;
    }
    const close = e.target.closest('[data-fitcheck-close]');
    if (close) {
      const m = close.closest('[data-fitcheck-modal]');
      if (m && m._fitBox) setFitcheckPanel(m._fitBox, false);
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-fitcheck-modal]:not([hidden])').forEach((m) => {
      if (m._fitBox) setFitcheckPanel(m._fitBox, false);
    });
  });

  // --- Fahrzeuglisten-Modal (Collection-Liste: "Alle anzeigen") ---
  let modalEl = null;
  function ensureModal() {
    if (modalEl) return modalEl;
    modalEl = document.createElement('div');
    modalEl.className = 'ebc-modal';
    modalEl.hidden = true;
    modalEl.innerHTML = `
      <div class="ebc-modal__backdrop" data-modal-close></div>
      <div class="ebc-modal__panel" role="dialog" aria-modal="true" aria-label="Passende Fahrzeuge">
        <button type="button" class="ebc-modal__close" aria-label="Schließen" data-modal-close>×</button>
        <div class="ebc-modal__content" data-modal-content></div>
      </div>`;
    document.body.appendChild(modalEl);
    modalEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]')) closeAppsModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalEl && !modalEl.hidden) closeAppsModal();
    });
    return modalEl;
  }
  function closeAppsModal() {
    if (!modalEl) return;
    modalEl.hidden = true;
    document.body.style.overflow = '';
  }
  async function openAppsModal(handle) {
    const m = ensureModal();
    const content = m.querySelector('[data-modal-content]');
    content.innerHTML = `<div class="finder__skeleton"><div class="finder__skeleton-row"></div></div>`;
    m.hidden = false;
    document.body.style.overflow = 'hidden';
    try {
      const res = await fetch(`/products/${handle}?view=apps`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      content.innerHTML = await res.text();
      wireAppsFilter(content);
      const input = content.querySelector('[data-apps-filter]');
      if (input) input.focus();
    } catch {
      content.innerHTML = '<p class="finder__error">Die Fahrzeugliste konnte nicht geladen werden.</p>';
    }
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-apps-modal]');
    if (btn) {
      e.preventDefault();
      openAppsModal(btn.dataset.handle);
    }
  });

  // Freitext-Filter über Fahrzeugtabellen (Modal + PDP)
  function wireAppsFilter(scope) {
    const input = scope.querySelector('[data-apps-filter]');
    if (!input || input.dataset.wired) return;
    input.dataset.wired = 'true';
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      // PDP zeigt initial nur 50 Zeilen — vor dem Filtern komplette Liste laden
      const expand = scope.querySelector('[data-apps-expand]');
      if (q !== '' && expand) expand.click();
      scope.querySelectorAll('tbody tr').forEach((tr) => {
        tr.hidden = q !== '' && !tr.textContent.toLowerCase().includes(q);
      });
    });
  }

  // --- Anwendungslisten (PDP): Expander + Markierung des gemerkten Fahrzeugs ---
  // PKW-Zeilen:  [Marke, Modell, Ausführung, Typ, FHZ-BJ, kW, PS, Zusatz, Achse]
  // Moto-Zeilen: [Marke, Modell, Ausführung, Bemerkung, ccm, Baujahr, Einbau, Hinweis]
  function appsRowHtml(r, isMoto, extraClass = '', tag = '') {
    const cls = extraClass ? ` class="${extraClass}"` : '';
    const first = `<strong>${esc(r[0])}</strong> ${esc(r[1])}${tag}`;
    return isMoto
      ? `<tr${cls}><td>${first}</td><td>${esc(r[2])}${r[3] ? ` (${esc(r[3])})` : ''}</td><td>${r[4] ?? ''}</td><td>${esc(r[5])}</td><td>${esc(r[6])}</td><td>${esc(r[7])}</td></tr>`
      : `<tr${cls}><td>${first}</td><td>${esc(r[2])}${r[3] ? ` (${esc(r[3])})` : ''}</td><td>${esc(r[4])}</td><td>${r[5] ? `${r[5]} kW / ${r[6]} PS` : ''}</td><td>${esc(r[7])}</td></tr>`;
  }
  function initApplicationsExpand() {
    document.querySelectorAll('[data-apps-expand]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-apps-root]');
        const dataEl = wrap.querySelector('[data-apps-full]');
        const tbody = wrap.querySelector('tbody');
        if (!dataEl || !tbody) return;
        const rows = JSON.parse(dataEl.textContent);
        const isMoto = wrap.dataset.bereich === 'moto';
        tbody.innerHTML = rows.map((r) => appsRowHtml(r, isMoto)).join('');
        btn.remove();
        markAppsVehicle(wrap);
      });
    });
  }

  // Die Zeilen tragen keine Fahrzeug-IDs → Textabgleich gegen das gemerkte Label.
  // Label PKW: "Marke [Modell] Ausführung (kW kW, Bj)" (Modell entfällt, wenn die
  // Ausführung damit beginnt) · Moto: "Marke Modell [Ausf] (Baujahr)".
  const normText = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
  function appsRowMatches(r, label, isMoto) {
    const full = normText(label);
    const base = full.replace(/\s*\([^)]*\)\s*$/, '');
    if (!r[0] || !base.startsWith(normText(r[0]))) return false;
    if (r[1] && !base.includes(normText(r[1]))) return false;
    if (r[2] && !base.includes(normText(r[2]))) return false;
    if (isMoto) {
      if (r[5] && !full.includes(normText(r[5]))) return false;
      return true;
    }
    if (r[5] && !full.includes(`${r[5]} kw`)) return false;
    return true;
  }
  function markAppsVehicle(root) {
    const isMoto = root.dataset.bereich === 'moto';
    const state = isMoto ? getMotoState() : getState();
    const mine = root.querySelector('[data-apps-mine]');
    const tbody = root.querySelector('tbody');
    if (mine) {
      mine.hidden = true;
      mine.textContent = '';
    }
    if (!tbody) return;
    // Aufräumen: erzeugte Kopien raus, Markierungen weg, Originalreihenfolge zurück.
    // DOM-Zeilen = rows[] in Reihenfolge (50 oder alle); data-idx merkt den Platz,
    // damit verschobene Zeilen beim Fahrzeugwechsel wieder einsortiert werden.
    tbody.querySelectorAll('tr.ebc-apps-row--pinned').forEach((tr) => tr.remove());
    let domRows = [...tbody.querySelectorAll('tr')];
    if (domRows.some((tr) => tr.dataset.idx === undefined)) domRows.forEach((tr, i) => (tr.dataset.idx = String(i)));
    domRows.forEach((tr) => {
      tr.classList.remove('ebc-apps-row--mine');
      tr.querySelector('.ebc-apps-row__tag')?.remove();
    });
    if (tbody.dataset.reordered === 'true') {
      domRows.sort((a, b) => +a.dataset.idx - +b.dataset.idx).forEach((tr) => tbody.appendChild(tr));
      tbody.dataset.reordered = '';
    }
    const dataEl = root.querySelector('[data-apps-full]');
    if (!state || !dataEl) return;
    // Nur markieren, wenn der Artikel laut Finder-Daten wirklich passt — der
    // Textabgleich könnte sonst eine Schwester-Variante treffen und der
    // Tabelle „✓ enthalten" bescheinigen, während das Badge „passt nicht" sagt.
    const sku = baseSku(root.dataset.sku);
    if (sku && !state.skus.map((x) => x.toUpperCase()).includes(sku)) return;
    let rows;
    try {
      rows = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    const label = state.vehicle.label;
    const hitIdx = [];
    rows.forEach((r, i) => {
      if (appsRowMatches(r, label, isMoto)) hitIdx.push(i);
    });
    if (hitIdx.length === 0) return;
    // Treffer nach oben: vorhandene Zeile verschieben + Badge; fehlt sie im DOM
    // (jenseits der ersten 50), eine Kopie erzeugen — nie beides.
    const tag = '<span class="ebc-apps-row__tag">Ihr Fahrzeug</span>';
    const byIdx = new Map(domRows.map((tr) => [+tr.dataset.idx, tr]));
    const top = hitIdx.map((i) => {
      let tr = byIdx.get(i);
      if (tr) {
        tr.classList.add('ebc-apps-row--mine');
        tr.querySelector('td')?.insertAdjacentHTML('beforeend', tag);
      } else {
        const tpl = document.createElement('template');
        tpl.innerHTML = appsRowHtml(rows[i], isMoto, 'ebc-apps-row--mine ebc-apps-row--pinned', tag);
        tr = tpl.content.firstElementChild;
      }
      return tr;
    });
    top.reverse().forEach((tr) => tbody.prepend(tr));
    tbody.dataset.reordered = 'true';
    if (mine) {
      mine.textContent = `✓ Ihr ${isMoto ? 'Motorrad' : 'Fahrzeug'}: ${label} — ${
        hitIdx.length === 1 ? 'in dieser Liste enthalten' : `${hitIdx.length} passende Einträge`
      }`;
      mine.hidden = false;
    }
  }

  // --- Init ---
  document.addEventListener('finder:changed', () => {
    renderChips();
    renderBadges();
    renderFitchecks();
    document.querySelectorAll('[data-apps-root]').forEach(markAppsVehicle);
  });
  const boot = () => {
    document.querySelectorAll('[data-vehicle-finder]').forEach((el) => new Finder(el));
    document.querySelectorAll('[data-collection-finder]').forEach((el) => new CollectionFinder(el));
    document.querySelectorAll('[data-ebc-related]').forEach((el) => new RelatedItems(el));
    if (document.querySelector('.product__media-item')) new PdpGallery();
    document
      .querySelectorAll(
        '[data-finder-make], [data-finder-model], [data-finder-type], [data-moto-kat], [data-moto-make], [data-moto-model], [data-moto-row]',
      )
      .forEach((sel) => new EbcSelect(sel));
    renderChips();
    renderBadges();
    renderFitchecks();
    initApplicationsExpand();
    document.querySelectorAll('[data-apps-root]').forEach((el) => {
      wireAppsFilter(el);
      markAppsVehicle(el);
    });
    revalidate();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
